/**
 * Deployment Grid Demo Page
 *
 * Demonstration page for the hex grid deployment system with drag-and-drop.
 * Shows creature roster, drag-and-drop placement, and deployment state.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as PIXI from 'pixi.js';
import { DeploymentGridWithDragDrop } from '../components/DeploymentGrid/DeploymentGridWithDragDrop';
import { HexGridRenderer } from '../components/DeploymentGrid/DeploymentGridRenderer';
import { BottomSheetRoster } from '../components/DeploymentGrid/BottomSheetRoster';
import { ReadyPanel } from '../components/DeploymentGrid/ReadyPanel';
import { ConnectionStatus } from '../components/DeploymentGrid/ConnectionStatus';
import { MatchSharing } from '../components/DeploymentGrid/MatchSharing';
import { PlayerDisplay } from '../components/DeploymentGrid/PlayerDisplay';
import { useDeploymentState } from '../components/DeploymentGrid/useDeploymentState';
import { useDeploymentSocketSync } from '../hooks/useDeploymentSocketSync';
import { useRosterFromUrl } from '../hooks/useRosterFromUrl';
import { useCreatureRoster } from '../hooks/useCreatureRoster';
import { useAllPlayerCreatures } from '../hooks/useAllPlayerCreatures';
import { transformManyToDeploymentCreatures, transformToDeploymentCreature } from '../services/creature-transform.service';
import { getCreatureSpriteLoader } from '../services/creature-sprite-loader';
import { CombatSocketClient, EffectFramesData } from '../services/combat-socket-client';
import { deploymentSocket } from '../services/deployment-socket';
import { pveSocket } from '../services/pve-socket';
import { CombatVisualizationManager } from '../services/combat-visualization-manager';
import { CombatGridRenderer } from '../components/CombatGrid/CombatGridRenderer';
import { ChallengeBattlefieldRenderer } from '../components/ChallengeBattlefield/ChallengeBattlefieldRenderer';
import type { ChallengeVisualAssets } from '../components/ChallengeBattlefield/ChallengeBattlefieldTypes';
import { CombatLog } from '../components/combat/CombatLog';
import { KillNotificationStack } from '../components/combat/KillNotificationStack';
import { MobileCombatHUD } from '../components/combat/MobileCombatHUD';
import { WaveIndicator } from '../components/ChallengeBattleHUD/WaveIndicator';
import { MobileCombatMenu } from '../components/combat/MobileCombatMenu';
import { TeamAbilityPanel } from '../components/combat/TeamAbilityPanel';
import { FRONTEND_FEATURE_FLAGS } from '../config/feature-flags';
import { getCombatLogService } from '../services/combat-log-service';
import { KillNotificationManager, CreatureLookupDocument } from '../services/kill-notification-manager';
import type { AxialCoordinate, DeploymentCreature, CreaturePlacement, MatchDeploymentStatus, CombatResult, CombatEvent } from '@drawn-of-war/shared';
import { ZoomControls } from '../components/ZoomControls';
import { VictoryOverlay } from '../components/VictoryOverlay';
import { BattlefieldHelp } from '../components/HelpOverlay';
import { VSScreenOverlay } from '../components/VSScreenOverlay';
import { RendererErrorBoundary } from '../components/ErrorBoundary';
// L4-SEAMLESS-005: preloadCreatureTextures removed - now handled by CombatVisualizationManager
import {
  createKeyboardPanHandler,
  createPinchZoomHandler,
  calculateWheelZoom,
  KeyboardPanHandler,
  PinchZoomHandler,
} from '../services/camera';
import { BackgroundPositionEditor } from '../services/terrain/background-position-editor';
import { useAuth } from '../contexts/AuthContext';
import { useViewport } from '../hooks/useViewport';
import { useLandscapeToast } from '../hooks/useLandscapeToast';
import { useChallengeStore } from '../stores/useChallengeStore';
import { useAdventureStore } from '../stores/useAdventureStore';
import { adventureSocket } from '../services/adventure-socket';
import { getApiUrl } from '../config/api';

// Initial mock creature data (sprites will be loaded dynamically)
const INITIAL_PLAYER1_CREATURES: DeploymentCreature[] = [
  { id: 'p1-warrior', name: 'Warrior', sprite: 'W', playerId: 'player1', stats: { health: 100, attack: 15, defense: 10 } },
  { id: 'p1-archer', name: 'Archer', sprite: 'A', playerId: 'player1', stats: { health: 80, attack: 20, defense: 5 } },
  { id: 'p1-mage', name: 'Mage', sprite: 'M', playerId: 'player1', stats: { health: 60, attack: 30, defense: 3 } },
  { id: 'p1-tank', name: 'Tank', sprite: 'T', playerId: 'player1', stats: { health: 150, attack: 10, defense: 20 } },
  { id: 'p1-rogue', name: 'Rogue', sprite: 'R', playerId: 'player1', stats: { health: 70, attack: 25, defense: 5 } },
  { id: 'p1-cleric', name: 'Cleric', sprite: 'C', playerId: 'player1', stats: { health: 90, attack: 12, defense: 8 } },
  { id: 'p1-paladin', name: 'Paladin', sprite: 'P', playerId: 'player1', stats: { health: 120, attack: 18, defense: 15 } },
  { id: 'p1-ranger', name: 'Ranger', sprite: 'G', playerId: 'player1', stats: { health: 85, attack: 22, defense: 7 } }
];

// Mock creature data for Player 2 (mirror of Player 1's roster)
const INITIAL_PLAYER2_CREATURES: DeploymentCreature[] = [
  { id: 'p2-warrior', name: 'Warrior', sprite: 'W', playerId: 'player2', stats: { health: 100, attack: 15, defense: 10 } },
  { id: 'p2-archer', name: 'Archer', sprite: 'A', playerId: 'player2', stats: { health: 80, attack: 20, defense: 5 } },
  { id: 'p2-mage', name: 'Mage', sprite: 'M', playerId: 'player2', stats: { health: 60, attack: 30, defense: 3 } },
  { id: 'p2-tank', name: 'Tank', sprite: 'T', playerId: 'player2', stats: { health: 150, attack: 10, defense: 20 } },
  { id: 'p2-rogue', name: 'Rogue', sprite: 'R', playerId: 'player2', stats: { health: 70, attack: 25, defense: 5 } },
  { id: 'p2-cleric', name: 'Cleric', sprite: 'C', playerId: 'player2', stats: { health: 90, attack: 12, defense: 8 } },
  { id: 'p2-paladin', name: 'Paladin', sprite: 'P', playerId: 'player2', stats: { health: 120, attack: 18, defense: 15 } },
  { id: 'p2-ranger', name: 'Ranger', sprite: 'G', playerId: 'player2', stats: { health: 85, attack: 22, defense: 7 } }
];

export const DeploymentGridDemoPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const viewport = useViewport();

  // Challenge mode detection (PvE)
  const mode = searchParams.get('mode');
  const isChallenge = mode === 'challenge';
  const challengeWeekId = searchParams.get('weekId');
  const challengeDay = searchParams.get('day');
  const challengeAttemptId = searchParams.get('attemptId');
  const challengeCreatureIds = searchParams.get('creatures')?.split(',').filter(Boolean) || [];

  // Adventure mode detection (co-op PvE)
  const isAdventure = mode === 'adventure';
  const adventureId = searchParams.get('adventureId');
  const adventureEncounterId = searchParams.get('encounterId');
  const adventureMatchId = searchParams.get('matchId');
  const adventureCreatureIds = searchParams.get('creatures')?.split(',').filter(Boolean) || [];

  // Adventure store data (persists from AdventureMapPage navigation)
  const adventureWorld = useAdventureStore(s => s.world);

  // Challenge encounter data (for PvE)
  const [challengeEncounter, setChallengeEncounter] = useState<any>(null);
  const [challengeLoading, setChallengeLoading] = useState(isChallenge);

  // PvP map config (for tile-enabled PvP battles)
  const [pvpMapConfig, setPvpMapConfig] = useState<any>(null);

  // Adventure encounter data (from Zustand store)
  const adventureEncounter = isAdventure && adventureEncounterId && adventureWorld
    ? adventureWorld.encounters[adventureEncounterId] || null
    : null;

  // Visual assets for themed battlefield (challenge mode)
  const [challengeVisualAssets, setChallengeVisualAssets] = useState<ChallengeVisualAssets | null>(null);
  const { fetchVisualAssets } = useChallengeStore();

  // Combat active state - needed early for canvas size calculation
  const [combatActive, setCombatActive] = useState(false);
  // Ref for synchronous access to combat state (avoids React async state race condition)
  const combatActiveRef = useRef(false);

  // Combat initialization phase tracking (L4-SEAMLESS-001)
  // Tracks the actual readiness of combat rendering, not just texture loading
  type CombatInitPhase = 'idle' | 'initializing' | 'ready' | 'failed';
  const [combatInitPhase, setCombatInitPhase] = useState<CombatInitPhase>('idle');
  const [combatInitError, setCombatInitError] = useState<string | null>(null);

  // VS Screen state
  const [showVSScreen, setShowVSScreen] = useState(false);
  const [vsLoadingProgress, setVsLoadingProgress] = useState(0);
  const [vsLoadingComplete, setVsLoadingComplete] = useState(false);

  // Mobile combat menu state
  const [isCombatMenuOpen, setIsCombatMenuOpen] = useState(false);

  // Wave tracking for challenge HUD
  const [waveState, setWaveState] = useState<{
    currentWave: number;
    totalWaves: number;
    enemiesRemaining: number;
    totalEnemiesInWave: number;
    nextWaveDelay: number | null; // ticks until next wave, null if no more
    isSpawning: boolean;
  } | null>(null);
  const waveScheduleRef = useRef<{ totalWaves: number; waves: { delay: number; enemyCount: number }[] } | null>(null);

  // Show landscape suggestion toast on mobile
  useLandscapeToast(combatActive);

  // Calculate combat canvas dimensions based on viewport
  // Mobile: Fill available space (different behavior for portrait vs landscape)
  // Desktop: Fixed 1200x700 (unchanged)
  const combatCanvasSize = React.useMemo(() => {
    if (viewport.isMobile) {
      const HUD_HEIGHT = 36; // MobileCombatHUD bar
      const DEPLOYMENT_UI_HEIGHT = 180; // Header, roster, etc.

      if (combatActive) {
        // COMBAT MODE: Maximize canvas to fill screen
        // Portrait: Full width, full height minus HUD (and TeamAbilityPanel if active)
        // Landscape: Full width, full height minus HUD
        // The hex grid (12x8) will be centered by CameraManager.fitToContent()
        const TEAM_PANEL_HEIGHT = (FRONTEND_FEATURE_FLAGS.USE_TEAM_ABILITY_PANEL && viewport.isPortrait) ? 80 : 0;
        return {
          width: Math.floor(viewport.width),
          height: Math.floor(viewport.height - HUD_HEIGHT - TEAM_PANEL_HEIGHT),
        };
      } else {
        // DEPLOYMENT MODE: Account for UI elements, use desktop-like ratio
        const DESKTOP_RATIO = 1200 / 700;
        const availableWidth = viewport.width - 16;
        const availableHeight = viewport.height - DEPLOYMENT_UI_HEIGHT;

        let width = availableWidth;
        let height = width / DESKTOP_RATIO;

        if (height > availableHeight) {
          height = availableHeight;
          width = height * DESKTOP_RATIO;
        }

        return {
          width: Math.floor(Math.max(width, 320)),
          height: Math.floor(Math.max(height, 187)),
        };
      }
    }

    // Desktop: Fixed 1200x700 (unchanged)
    return { width: 1200, height: 700 };
  }, [viewport.isMobile, viewport.isPortrait, viewport.width, viewport.height, combatActive]);

  // Fetch challenge encounter data and visual assets when in challenge mode
  useEffect(() => {
    if (!isChallenge || !challengeWeekId || !challengeDay) {
      setChallengeLoading(false);
      return;
    }

    const fetchChallengeData = async () => {
      setChallengeLoading(true);
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

        // Fetch encounter data
        const encounterResponse = await fetch(
          `${apiUrl}/api/challenges/${challengeWeekId}/encounters/${challengeDay}`,
          {
            headers: {
              'Authorization': `Bearer ${await getIdToken()}`,
            },
          }
        );

        if (!encounterResponse.ok) {
          throw new Error('Failed to load challenge encounter');
        }

        const encounterData = await encounterResponse.json();
        console.log('[DeploymentDemo] Challenge encounter loaded:', encounterData.encounter);
        setChallengeEncounter(encounterData.encounter);

        // Fetch visual assets (optional - may not be generated for all weeks)
        try {
          console.log('[DeploymentDemo] Fetching visual assets for themed battlefield...');
          const visualAssets = await fetchVisualAssets(challengeWeekId, parseInt(challengeDay));

          if (visualAssets) {
            console.log('[DeploymentDemo] ✓ Visual assets loaded for themed battlefield');
            setChallengeVisualAssets(visualAssets);
          } else {
            console.log('[DeploymentDemo] No visual assets available, will use basic renderer');
            setChallengeVisualAssets(null);
          }
        } catch (visualError) {
          console.log('[DeploymentDemo] Visual assets not available:', visualError);
          setChallengeVisualAssets(null);
        }
      } catch (error) {
        console.error('[DeploymentDemo] Failed to load challenge data:', error);
      } finally {
        setChallengeLoading(false);
      }
    };

    fetchChallengeData();
  }, [isChallenge, challengeWeekId, challengeDay, user, fetchVisualAssets]);

  // Load challenge tiles onto the deployment grid when encounter data arrives
  // (handles case where challenge data loads AFTER the renderer is already initialized)
  useEffect(() => {
    if (deploymentTilesLoadedRef.current) return;
    const renderer = deploymentRendererRef.current;
    const tiles = challengeEncounter?.mapConfig?.tiles;
    if (renderer && tiles && Object.keys(tiles).length > 0) {
      deploymentTilesLoadedRef.current = true;
      renderer.tileBackgroundUrl = '/assets/challenges/volcanic/battlefield-bg.jpeg';
      renderer.loadTiles(tiles);
      console.log('[DeploymentDemo] ✓ Challenge tiles loaded onto deployment grid (via effect)');
    }
  }, [challengeEncounter]);

  // Fetch PvP map config for non-challenge, non-adventure battles
  useEffect(() => {
    if (isChallenge || isAdventure) return;

    const fetchPvpMap = async () => {
      try {
        const apiUrl = getApiUrl();
        const resp = await fetch(`${apiUrl}/api/battles/pvp-map`);
        if (!resp.ok) throw new Error('Failed to load PvP map');
        const data = await resp.json();
        if (data.success && data.mapConfig) {
          console.log('[DeploymentDemo] PvP map loaded:', data.mapConfig.name);
          setPvpMapConfig(data.mapConfig);
        }
      } catch (err) {
        console.warn('[DeploymentDemo] Could not load PvP map, using defaults:', err);
      }
    };

    fetchPvpMap();
  }, [isChallenge, isAdventure]);

  // Get or generate matchId and playerId from URL params
  const matchIdFromUrl = searchParams.get('matchId');
  const playerIdFromUrl = searchParams.get('playerId') as 'player1' | 'player2' | null;

  const [matchId] = useState<string>(() => {
    // For challenge mode, use attemptId as matchId for consistency
    if (isChallenge && challengeAttemptId) {
      return `challenge-${challengeAttemptId}`;
    }
    // For adventure mode, use matchId from URL (provided by backend)
    if (isAdventure && adventureMatchId) {
      return adventureMatchId;
    }
    return matchIdFromUrl || uuidv4();
  });

  // Redirect to add URL params if not present (PvP only - challenge mode uses different params)
  useEffect(() => {
    // Skip redirect logic for challenge/adventure mode - they have their own params
    if (isChallenge || isAdventure) {
      return;
    }

    if (!matchIdFromUrl || !playerIdFromUrl) {
      const currentPath = window.location.pathname;
      const basePath = currentPath.includes('deployment-grid') ? '/deployment-grid' : '/deployment';

      // Preserve existing query parameters (like creatures)
      const params = new URLSearchParams(searchParams);
      if (!matchIdFromUrl) {
        params.set('matchId', matchId);
      }
      if (!playerIdFromUrl) {
        params.set('playerId', 'player1');
      }

      navigate(`${basePath}?${params.toString()}`, { replace: true });
    }
  }, [matchIdFromUrl, playerIdFromUrl, matchId, navigate, searchParams, isChallenge]);

  const [currentPlayerId] = useState<'player1' | 'player2'>(() => {
    // Normalize playerId - only accept 'player1' or 'player2', default to 'player1'
    if (playerIdFromUrl === 'player1' || playerIdFromUrl === 'player2') {
      return playerIdFromUrl;
    }
    return 'player1';
  });

  // Parse creature IDs from URL (or use challenge/adventure creature IDs)
  const urlRoster = useRosterFromUrl();
  // In challenge/adventure mode, use the creature IDs from mode-specific params
  const creatureIds = isChallenge && challengeCreatureIds.length > 0
    ? challengeCreatureIds
    : isAdventure && adventureCreatureIds.length > 0
      ? adventureCreatureIds
      : urlRoster.creatureIds;
  const hasCreatures = isChallenge
    ? challengeCreatureIds.length > 0
    : isAdventure
      ? adventureCreatureIds.length > 0
      : urlRoster.hasCreatures;

  // Load creatures from library (uses authenticated user context):
  // - If creature IDs provided in URL: load specific creatures (useCreatureRoster)
  // - If no IDs in URL: load ALL creatures for user (useAllPlayerCreatures)
  const { creatures: specificCreatures, loading: loadingSpecific, error: specificError } = useCreatureRoster(
    creatureIds,
    hasCreatures
  );

  const { creatures: allCreatures, loading: loadingAll, error: allError } = useAllPlayerCreatures(
    !hasCreatures  // Only load all if no specific IDs provided
  );

  // Combine loading states
  const loadedCreatures = hasCreatures ? specificCreatures : allCreatures;
  const loadingCreatures = hasCreatures ? loadingSpecific : loadingAll;
  const loadError = hasCreatures ? specificError : allError;

  // Transform loaded creatures to deployment format
  const loadedDeploymentCreatures = React.useMemo(() => {
    if (loadedCreatures.length > 0) {
      return transformManyToDeploymentCreatures(loadedCreatures, currentPlayerId);
    }
    return [];
  }, [loadedCreatures, currentPlayerId]);

  // Initialize creature rosters as empty (will be populated by loading hooks)
  const [player1Creatures, setPlayer1Creatures] = useState<DeploymentCreature[]>([]);
  const [player2Creatures, setPlayer2Creatures] = useState<DeploymentCreature[]>([]);
  const [opponentPlacements, setOpponentPlacements] = useState<CreaturePlacement[]>([]);
  const [deploymentStatus, setDeploymentStatus] = useState<MatchDeploymentStatus | null>(null);

  // RACE CONDITION FIX: Store raw socket data in refs for synchronous access during combat init
  // React setState is async, so opponentPlacements might not be updated when combat starts
  const lastSocketDataRef = useRef<{
    player1Placements: CreaturePlacement[];
    player2Placements: CreaturePlacement[];
  } | null>(null);
  const [combatState, setCombatState] = useState<any | null>(null);
  // Note: combatActive state is declared earlier (line ~85) for canvas size calculation
  const [combatResult, setCombatResult] = useState<CombatResult | null>(null);
  const [seriesRecord, setSeriesRecord] = useState<{
    player1Wins: number;
    player2Wins: number;
    draws: number;
  } | null>(null);
  const [roundNumber, setRoundNumber] = useState(1);

  // Combat visualization state
  const [combatVisualizationManager, setCombatVisualizationManager] = useState<CombatVisualizationManager | null>(null);
  const [combatGridRenderer, setCombatGridRenderer] = useState<CombatGridRenderer | ChallengeBattlefieldRenderer | null>(null);
  const combatCanvasRef = useRef<HTMLDivElement>(null);
  const deploymentRendererRef = useRef<HexGridRenderer | null>(null);
  const deploymentTilesLoadedRef = useRef(false);

  // Camera control refs for combat view (L4-ZOOM-005)
  const combatKeyboardPanRef = useRef<KeyboardPanHandler | null>(null);
  const combatPinchZoomRef = useRef<PinchZoomHandler | null>(null);
  const combatAnimationFrameRef = useRef<number | null>(null);

  // Combat socket client - create instance once and persist across renders
  const combatSocketRef = useRef<CombatSocketClient | null>(null);
  if (!combatSocketRef.current) {
    combatSocketRef.current = new CombatSocketClient(
      getApiUrl(),
      matchId,
      { autoConnect: false, reconnect: true }
    );
  }

  // PvE socket adapter ref - stores adapter that matches CombatSocketClient interface
  // Used for challenge mode combat visualization
  const pveSocketAdapterRef = useRef<{
    onCombatState: (callback: (state: any) => void) => void;
    onCombatCompleted: (callback: (result: any) => void) => void;
    onError: (callback: (error: Error) => void) => void;
    signalReady: () => void;
    onEffectFrames: (callback: (data: any) => void) => void;
    requestEffectFrames: () => void;
    onCombatEvents: (callback: (events: any[]) => void) => void;
    // PVE-FIX-007: Clear state callbacks on cleanup to prevent stale callbacks on re-init
    clearStateCallbacks?: () => void;
  } | null>(null);

  // Adventure socket adapter ref - stores adapter for adventure mode combat visualization
  const adventureSocketAdapterRef = useRef<{
    onCombatState: (callback: (state: any) => void) => void;
    onCombatCompleted: (callback: (result: any) => void) => void;
    onError: (callback: (error: Error) => void) => void;
    signalReady: () => void;
    onEffectFrames: (callback: (data: any) => void) => void;
    requestEffectFrames: () => void;
    onCombatEvents: (callback: (events: any[]) => void) => void;
    clearStateCallbacks?: () => void;
  } | null>(null);

  // PvE enemy placements ref - stores enemy data immediately when received from API
  // RACE CONDITION FIX: React setState is async, so opponentPlacements may not be updated
  // when the combat lifecycle effect runs. This ref provides synchronous access.
  const pveEnemyPlacementsRef = useRef<CreaturePlacement[]>([]);

  // Kill notification manager (L4-KILL-005)
  const killNotificationManagerRef = useRef<KillNotificationManager>(
    new KillNotificationManager({
      maxNotifications: 5,
      displayDuration: 3000,
      fadeDuration: 500,
    })
  );

  // Bottom sheet deployment state
  const [draggedGalleryCreature, setDraggedGalleryCreature] = useState<any | null>(null);
  const [creatureLimit, setCreatureLimit] = useState<number>(6);

  // Mobile tap-to-select state (TASK-TDI-3) - for gallery selection
  const [selectedCreatureId, setSelectedCreatureId] = useState<string | null>(null);
  const [selectedCreature, setSelectedCreature] = useState<any | null>(null);

  // State for creature repositioning (separate from gallery selection)
  // When set, the creature with this ID is selected for tap-to-reposition
  const [repositionCreatureId, setRepositionCreatureId] = useState<string | null>(null);

  // Update creatures when loaded from library (always loads all creatures now)
  useEffect(() => {
    if (!loadingCreatures && loadedDeploymentCreatures.length > 0) {
      console.log('[DeploymentDemo] Loaded creatures from Firestore:', loadedDeploymentCreatures.length);
      console.log('[DeploymentDemo] Source:', hasCreatures ? 'specific IDs from URL' : 'all player creatures');

      if (currentPlayerId === 'player1') {
        setPlayer1Creatures(loadedDeploymentCreatures);
      } else {
        setPlayer2Creatures(loadedDeploymentCreatures);
      }
    }
  }, [loadingCreatures, loadedDeploymentCreatures, currentPlayerId, hasCreatures]);

  // NOTE: Mock creature sprite loading removed - all creatures now loaded from Firestore with full sprite data

  // Use deployment state hook
  const {
    deploymentState,
    dragState,
    placeCreature,
    removeCreature,
    validatePlacement,
    startDrag,
    updateDrag,
    endDrag,
    cancelDrag,
    clearAllPlacements,
    getPlacementCount,
    markReady,
    markUnready,
    markLocked,
    canMarkReady,
    syncPlacementsFromServer,
    resetForNewRound
  } = useDeploymentState(player1Creatures, player2Creatures, currentPlayerId,
    // Adventure mode uses orientation from encounter config (top_vs_bottom for co-op)
    adventureEncounter?.battlefieldConfig ? {
      orientation: (adventureEncounter.battlefieldConfig.orientation || 'left_vs_right') as 'left_vs_right' | 'top_vs_bottom',
      playerDeployCols: adventureEncounter.battlefieldConfig.playerDeployCols,
      playerDeployRows: adventureEncounter.battlefieldConfig.playerDeployRows,
      enemyDeployRows: adventureEncounter.battlefieldConfig.enemyDeployRows,
      gridHeight: adventureEncounter.battlefieldConfig.height,
      gridWidth: adventureEncounter.battlefieldConfig.width,
    }
    // Challenge mode: derive zones from encounter's playerSpawn
    : challengeEncounter?.mapConfig?.playerSpawn ? (() => {
      const p1Cols = challengeEncounter.mapConfig.playerSpawn.map((h: any) => h.q);
      const p2Hexes = Array.isArray(challengeEncounter.mapConfig.enemySpawn)
        ? challengeEncounter.mapConfig.enemySpawn.map((h: any) => h.position?.q ?? h.q)
        : [];
      return {
        orientation: 'left_vs_right' as const,
        player1ColRange: { min: Math.min(...p1Cols), max: Math.max(...p1Cols) },
        player2ColRange: p2Hexes.length > 0
          ? { min: Math.min(...p2Hexes), max: Math.max(...p2Hexes) }
          : undefined,
        gridWidth: challengeEncounter.mapConfig.columns,
        gridHeight: challengeEncounter.mapConfig.rows,
      };
    })()
    // PvP mode: derive deployment zones from map config spawn data
    : pvpMapConfig?.playerSpawn ? (() => {
      const p1Cols = pvpMapConfig.playerSpawn.map((h: any) => h.q);
      const p2Hexes = Array.isArray(pvpMapConfig.enemySpawn)
        ? pvpMapConfig.enemySpawn.map((h: any) => h.position?.q ?? h.q)
        : [];
      return {
        orientation: 'left_vs_right' as const,
        player1ColRange: { min: Math.min(...p1Cols), max: Math.max(...p1Cols) },
        player2ColRange: { min: Math.min(...p2Hexes), max: Math.max(...p2Hexes) },
        gridWidth: pvpMapConfig.columns,
        gridHeight: pvpMapConfig.rows,
      };
    })() : undefined,
    // Terrain data for blocking placement on impassable hexes
    challengeEncounter?.mapConfig?.terrain ?? pvpMapConfig?.terrain
  );

  // Use Socket.IO sync hook (disabled in challenge mode - PvE doesn't need opponent sync)
  const {
    isConnected,
    opponentConnected,
    opponentPlayerId,
    error: socketError,
    player1DisplayName,
    player2DisplayName,
    emitPlacement,
    emitRemoval,
    emitReady,
    emitUnready,
    emitRequestRematch,
    emitLeaveMatch
  } = useDeploymentSocketSync({
    matchId,
    playerId: currentPlayerId,
    enabled: !isChallenge && !isAdventure, // Disable for PvE challenge/adventure modes
    onStateReceived: (data) => {
      console.log('[DeploymentDemo] ===== INITIAL STATE RECEIVED =====');
      console.log('[DeploymentDemo] Full data:', data);

      // RACE CONDITION FIX: Store raw socket data in ref for synchronous access
      lastSocketDataRef.current = {
        player1Placements: data.player1Placements,
        player2Placements: data.player2Placements
      };
      console.log('[DeploymentDemo] Stored socket data in ref:', {
        player1Count: data.player1Placements.length,
        player2Count: data.player2Placements.length
      });

      // Update creature limit from server (TASK-006: Dynamic creature limit)
      setCreatureLimit(data.creatureLimit ?? 6);
      console.log('[DeploymentDemo] Creature limit set to:', data.creatureLimit ?? 6);

      // Restore BOTH players' placements from server
      const currentPlayerPlacementsData = currentPlayerId === 'player1' ? data.player1Placements : data.player2Placements;
      const opponentId = currentPlayerId === 'player1' ? 'player2' : 'player1';
      const opponentPlacementsData = opponentId === 'player1' ? data.player1Placements : data.player2Placements;

      // Sync current player's placements
      console.log('[DeploymentDemo] Syncing current player placements:', {
        playerId: currentPlayerId,
        count: currentPlayerPlacementsData.length,
        creatures: currentPlayerPlacementsData.map(p => p.creature.name)
      });
      syncPlacementsFromServer(currentPlayerId, currentPlayerPlacementsData);

      // Update opponent placements
      console.log('[DeploymentDemo] Setting opponent placements:', {
        opponentId,
        count: opponentPlacementsData.length,
        placements: opponentPlacementsData.map(p => ({
          creatureId: p.creature.id,
          creatureName: p.creature.name,
          hasBattlefieldViews: !!p.creature.battlefieldDirectionalViews
        }))
      });
      setOpponentPlacements(opponentPlacementsData);
      setDeploymentStatus(data.deploymentStatus);
    },
    onOpponentPlaced: (placement) => {
      console.log('[DeploymentDemo] ===== OPPONENT PLACED =====');
      console.log('[DeploymentDemo] Full placement:', placement);
      console.log('[DeploymentDemo] Creature ID:', placement.creature.id);
      console.log('[DeploymentDemo] Creature name:', placement.creature.name);
      console.log('[DeploymentDemo] Has battlefieldDirectionalViews:', !!placement.creature.battlefieldDirectionalViews);
      if (placement.creature.battlefieldDirectionalViews) {
        console.log('[DeploymentDemo] Directional views:', placement.creature.battlefieldDirectionalViews);
        console.log('[DeploymentDemo] E direction:', placement.creature.battlefieldDirectionalViews.E);
      } else {
        console.log('[DeploymentDemo] ❌ NO battlefieldDirectionalViews - this is the problem!');
      }
      setOpponentPlacements(prev => [...prev, placement]);
    },
    onOpponentRemoved: (creatureId) => {
      console.log('[DeploymentDemo] Opponent removed:', creatureId);
      setOpponentPlacements(prev => prev.filter(p => p.creature.id !== creatureId));
    },
    onOpponentUpdated: (placements) => {
      console.log('[DeploymentDemo] Opponent updated placements:', placements);
      setOpponentPlacements(placements);
    },
    onStatusChanged: (status) => {
      console.log('[DeploymentDemo] Status changed:', status);
      setDeploymentStatus(status);
    },
    onCombatStarted: (data) => {
      console.log('[DeploymentDemo] ===== COMBAT STARTED =====');
      console.log('[DeploymentDemo] Match ID:', data.matchId);

      // TASK-VIS-004: Clear combat log for new combat session
      getCombatLogService().clear();
      console.log('[DeploymentDemo] ✓ Combat log cleared');

      // L4-SEAMLESS-002: Show VS screen AND start combat init simultaneously
      // Combat initializes BEHIND the VS screen, which only dismisses when ready
      setShowVSScreen(true);
      setVsLoadingProgress(0);
      setVsLoadingComplete(false);
      combatActiveRef.current = true;
      setCombatActive(true);  // Start combat init NOW (runs behind VS screen)

      // Clear deployment zone color overlays immediately (deployment grid stays visible
      // behind VS screen until combat renderer takes over)
      deploymentRendererRef.current?.clearDeploymentZones();

      console.log('[DeploymentDemo] ✓ VS screen displayed, combat init starting');

      // L4-SEAMLESS-005: Removed duplicate texture preloading
      // Texture preloading now happens in CombatVisualizationManager.start()
      // This avoids double-loading and lets the viz manager handle it properly
      // The combatInitPhase state tracks when initialization (including preload) is complete
    },
    onRoundReset: (data) => {
      console.log('[DeploymentDemo] ===== ROUND RESET (FIGHT AGAIN) =====');
      console.log('[DeploymentDemo] Round:', data.roundNumber);
      console.log('[DeploymentDemo] Series Record:', data.seriesRecord);

      // Update series tracking state
      setSeriesRecord(data.seriesRecord);
      setRoundNumber(data.roundNumber);

      // Update creature limit from server (TASK-006: Dynamic creature limit)
      setCreatureLimit(data.creatureLimit ?? 6);
      console.log('[DeploymentDemo] Creature limit set to:', data.creatureLimit ?? 6);

      // Clear combat state
      setCombatResult(null);
      combatActiveRef.current = false;
      setCombatActive(false);
      setCombatState(null);

      // Reset placements for new round
      setOpponentPlacements([]);
      // CRITICAL FIX: Reset current player's deployment state (placements + locked/ready flags)
      resetForNewRound(currentPlayerId);

      // Update deployment status
      setDeploymentStatus(data.deploymentStatus);

      console.log('[DeploymentDemo] ✓ Reset complete, ready for round', data.roundNumber);
    },
    onOpponentLeft: (playerId) => {
      console.log('[DeploymentDemo] ===== OPPONENT LEFT =====');
      console.log('[DeploymentDemo] Player who left:', playerId);

      // Navigate back to lobby since opponent left
      navigate('/home');
    },
    onError: (message) => {
      console.error('[DeploymentDemo] Socket error:', message);
    }
  });

  // EARLY CONNECT: Connect to combat socket during deployment phase (PvP only)
  // This ensures we receive all combat state updates from tick 0
  // Skip in challenge mode - PvE uses different combat flow
  useEffect(() => {
    // Skip early combat socket connection in challenge/adventure mode - they use different flows
    if (isChallenge || isAdventure) {
      console.log('[DeploymentDemo] Skipping early combat socket - PvE mode uses different flow');
      return;
    }

    console.log('[DeploymentDemo] Early combat socket connection - matchId:', matchId);

    const combatSocket = combatSocketRef.current;
    if (!combatSocket) {
      console.error('[DeploymentDemo] Combat socket not initialized');
      return;
    }

    const connectEarly = async () => {
      try {
        console.log('[DeploymentDemo] Connecting to combat socket early (during deployment)...');
        await combatSocket.connect();
        console.log('[DeploymentDemo] ✓ Combat socket connected early - ready to receive states from tick 0');
      } catch (error) {
        console.error('[DeploymentDemo] ✗ Failed to connect to combat socket early:', error);
      }
    };

    connectEarly();

    // Cleanup: disconnect combat socket when component unmounts
    return () => {
      console.log('[DeploymentDemo] Component unmounting - disconnecting combat socket');
      combatSocket.disconnect();
    };
  }, [matchId, isChallenge]);

  // NOTE: Combat start listener is now registered via useDeploymentSocketSync's onCombatStarted callback
  // This ensures the listener is registered AFTER the socket is connected, avoiding race conditions

  // Separate combat lifecycle management - only runs when combat becomes active
  useEffect(() => {
    // Check both ref (sync) and state to ensure combat is truly active
    if (!combatActive && !combatActiveRef.current) {
      console.log('[DeploymentDemo] Combat not active, skipping combat setup');
      return;
    }

    console.log('[DeploymentDemo] Combat lifecycle: Starting initialization');
    setCombatInitPhase('initializing');
    setCombatInitError(null);
    let isCleanedUp = false;
    let renderer: CombatGridRenderer | ChallengeBattlefieldRenderer | null = null;
    let vizManager: CombatVisualizationManager | null = null;

    const initializeCombat = async () => {
      if (isCleanedUp) {
        console.log('[DeploymentDemo] Combat lifecycle: Already cleaned up, aborting init');
        return;
      }

      // NOTE: Combat socket already joined early (during deployment phase)
      // No need to join again here - we're already listening for states from tick 0

      // Initialize combat grid renderer (themed or basic)
      try {
        const gridConfig = {
          width: adventureEncounter?.battlefieldConfig?.width
            ?? challengeEncounter?.mapConfig?.columns
            ?? pvpMapConfig?.columns
            ?? 18,
          height: adventureEncounter?.battlefieldConfig?.height
            ?? challengeEncounter?.mapConfig?.rows
            ?? pvpMapConfig?.rows
            ?? 12,
          hexSize: 38,
          orientation: 'flat-top' as const,
          projection: 'isometric' as const
        };

        // Use viewport-aware dimensions for mobile support
        console.log('[DeploymentDemo] Combat canvas size:', combatCanvasSize);

        // Check if we should use themed battlefield renderer
        // Use it when we have visual assets or a map visual background, BUT NOT when
        // hex tiles are present — tiles route through the basic CombatGridRenderer path
        // to avoid z-order conflicts with ChallengeBattlefieldRenderer's visual layers
        const hasMapVisual = challengeEncounter?.mapConfig?.visual?.backgroundImage;
        const hasChallengeTiles = challengeEncounter?.mapConfig?.tiles && Object.keys(challengeEncounter.mapConfig.tiles).length > 0;
        const useThemedBattlefield = isChallenge && !hasChallengeTiles && (challengeVisualAssets || hasMapVisual);

        if (useThemedBattlefield) {
          console.log('[DeploymentDemo] Initializing themed battlefield renderer with visual assets...');

          // Create ChallengeBattlefieldRenderer instance
          const themedRenderer = new ChallengeBattlefieldRenderer({
            canvasWidth: combatCanvasSize.width,
            canvasHeight: combatCanvasSize.height,
            hexGridConfig: gridConfig,
            gridBounds: {
              minX: 0,
              maxX: gridConfig.width - 1,
              minY: 0,
              maxY: gridConfig.height - 1,
            },
            hexToPixel: (hex) => {
              // Convert hex coordinates to pixel coordinates (simplified)
              const x = hex.q * gridConfig.hexSize * 1.5;
              const y = (hex.r + hex.q / 2) * gridConfig.hexSize * Math.sqrt(3);
              return { x, y };
            },
            getHexCorners: (center) => {
              // Generate hex corner points (simplified)
              const corners = [];
              for (let i = 0; i < 6; i++) {
                const angle = (Math.PI / 3) * i;
                corners.push({
                  x: center.x + gridConfig.hexSize * Math.cos(angle),
                  y: center.y + gridConfig.hexSize * Math.sin(angle),
                });
              }
              return corners;
            },
            showGrid: false, // Hide grid during combat
            animateHazards: true,
            enableAtmosphere: true,
          });

          // Initialize the renderer
          await themedRenderer.initialize();

          // Load themed visual assets if available (parallax, ground tiles, props, hazards)
          if (challengeVisualAssets) {
            await themedRenderer.loadVisualAssets(challengeVisualAssets, (progress) => {
              console.log(
                `[DeploymentDemo] Loading assets: ${progress.loaded}/${progress.total} (${progress.phase})`
              );
            });
          }

          // Load map visual background if available (hand-positioned from battlefield editor)
          const mapVisual = challengeEncounter?.mapConfig?.visual;
          if (mapVisual?.backgroundImage) {
            await themedRenderer.loadMapVisual(mapVisual);
          }

          console.log('[DeploymentDemo] ✓ Themed battlefield renderer initialized');

          // Assign themed renderer (types updated to support both renderers)
          renderer = themedRenderer;
        } else {
          console.log('[DeploymentDemo] Initializing basic combat grid renderer...');

          // Use basic renderer
          const visual = challengeEncounter?.mapConfig?.visual ?? pvpMapConfig?.visual;
          renderer = new CombatGridRenderer({
            canvasWidth: combatCanvasSize.width,
            canvasHeight: combatCanvasSize.height,
            hexGridConfig: gridConfig,
            showCoordinates: false,
            ...(visual?.backgroundImage ? {
              backgroundImageOverride: visual.backgroundImage,
              bgOffsetX: visual.bgOffsetX,
              bgOffsetY: visual.bgOffsetY,
              bgScale: visual.bgScale,
              bgBrightness: visual.bgBrightness,
              bgContrast: visual.bgContrast,
              bgSaturation: visual.bgSaturation,
              bgHue: visual.bgHue,
            } : {}),
          });

          await renderer.init();

          // Load PvP map tiles if available
          if (pvpMapConfig?.tiles && Object.keys(pvpMapConfig.tiles).length > 0) {
            await (renderer as CombatGridRenderer).loadTiles(pvpMapConfig.tiles);
            console.log('[DeploymentDemo] ✓ PvP map tiles loaded');
          }

          // Load challenge map tiles (same proven path as PvP tiles)
          if (!pvpMapConfig?.tiles && challengeEncounter?.mapConfig?.tiles) {
            const challengeTiles = challengeEncounter.mapConfig.tiles;
            if (Object.keys(challengeTiles).length > 0) {
              (renderer as CombatGridRenderer).tileBackgroundUrl = '/assets/challenges/volcanic/battlefield-bg.jpeg';
              await (renderer as CombatGridRenderer).loadTiles(challengeTiles);
              console.log('[DeploymentDemo] ✓ Challenge map tiles loaded via basic renderer');
            }
          }

          console.log('[DeploymentDemo] ✓ Basic combat grid renderer initialized');
        }

        if (isCleanedUp) {
          console.log('[DeploymentDemo] Combat lifecycle: Cleaned up during renderer init, aborting');
          renderer.destroy();
          // NOTE: Socket connection managed by early connection effect - don't leave here
          return;
        }

        // CRITICAL: Don't add canvas here - it will be orphaned by React re-render
        // Store canvas for later addition after React finishes rendering
        console.log('[DeploymentDemo] Storing canvas reference for post-render addition');

        setCombatGridRenderer(renderer);

        // Create socket adapter for CombatVisualizationManager
        // For PvE (challenge mode), use the PvE socket adapter
        // For PvP, use the standard combat socket
        console.log('[DeploymentDemo] Creating socket adapter... isChallenge:', isChallenge);

        let socketAdapter: any;

        if (isChallenge && pveSocketAdapterRef.current) {
          // Use PvE socket adapter for challenge mode
          console.log('[DeploymentDemo] Using PvE socket adapter for challenge mode');
          socketAdapter = pveSocketAdapterRef.current;
        } else if (isAdventure && adventureSocketAdapterRef.current) {
          // Use adventure socket adapter for adventure mode
          console.log('[DeploymentDemo] Using adventure socket adapter for adventure mode');
          socketAdapter = adventureSocketAdapterRef.current;
        } else {
          // Use standard combat socket for PvP
          const combatSocket = combatSocketRef.current;
          if (!combatSocket) {
            throw new Error('Combat socket not initialized');
          }

          socketAdapter = {
            onCombatState: (callback: (state: any) => void) => {
              console.log('[DeploymentDemo] Socket adapter: registering state callback');
              combatSocket.onCombatState((state) => {
                console.log('[DeploymentDemo] Socket adapter: received state, forwarding to callback');
                callback(state);
              });
            },
            onCombatCompleted: (callback: (result: any) => void) => {
              console.log('[DeploymentDemo] Socket adapter: registering completion callback');
              combatSocket.onCombatCompleted((result) => {
                console.log('[DeploymentDemo] Socket adapter: received completion, forwarding to callback');
                callback(result);
              });
            },
            onError: (callback: (error: Error) => void) => {
              console.log('[DeploymentDemo] Socket adapter: registering error callback');
              combatSocket.onError((error) => {
                console.error('[DeploymentDemo] Socket adapter: received error, forwarding to callback');
                callback(error);
              });
            },
            signalReady: () => {
              console.log('[DeploymentDemo] Socket adapter: signaling client ready to backend');
              combatSocket.signalReady();
            },
            // TASK-EFD-003: EffectFrames reliable delivery
            onEffectFrames: (callback: (data: EffectFramesData) => void) => {
              console.log('[DeploymentDemo] Socket adapter: registering effectFrames callback');
              combatSocket.onEffectFrames((data: EffectFramesData) => {
                console.log(`[DeploymentDemo] Socket adapter: received effectFrames for ${Object.keys(data.frames).length} units`);
                callback(data);
              });
            },
            // TASK-EFD-004: Request missing effectFrames
            requestEffectFrames: () => {
              console.log('[DeploymentDemo] Socket adapter: requesting missing effectFrames');
              combatSocket.requestEffectFrames();
            },
            // L4-JUICE-009: Wire combat events for damage numbers, screen shake, hit stop
            onCombatEvents: (callback: (events: CombatEvent[]) => void) => {
              console.log('[DeploymentDemo] Socket adapter: registering combat events callback');
              combatSocket.onCombatEvents((events) => {
                console.log(`[DeploymentDemo] Socket adapter: received ${events.length} combat events`);
                callback(events);
              });
            }
          };
        }

        // Create combat visualization manager
        console.log('[DeploymentDemo] Creating CombatVisualizationManager...');
        vizManager = new CombatVisualizationManager(
          socketAdapter as any,
          renderer as any // Grid renderer (stage is obtained from renderer.getStage())
        );

        if (isCleanedUp) {
          console.log('[DeploymentDemo] Combat lifecycle: Cleaned up during viz manager creation, aborting');
          vizManager.destroy();
          renderer.destroy();
          // NOTE: Socket connection managed by early connection effect - don't leave here
          return;
        }

        // CRITICAL FIX: Load creature sprite data into the visualization manager
        // This allows combat to render actual creature sprites instead of placeholders
        // IMPORTANT: Include creatures from BOTH local rosters AND opponent placements
        // because opponent placements may contain creatures with sprite data we don't have locally

        // RACE CONDITION FIX: currentPlayerState.placements might be empty if deployment state
        // hasn't fully synced when combat starts. Use multiple fallback sources to ensure
        // we have creature data with battlefieldDirectionalViews.
        let currentPlayerPlacementsCreatures: DeploymentCreature[] = [];

        if (currentPlayerState.placements && currentPlayerState.placements.length > 0) {
          // Preferred: Use synced deployment state
          currentPlayerPlacementsCreatures = currentPlayerState.placements.map(p => p.creature);
          console.log('[DeploymentDemo] Using current player placements from deploymentState:', currentPlayerPlacementsCreatures.length);
        } else if (deploymentStatus) {
          // Fallback: Use deployment status (from socket sync)
          const placementsData =
            currentPlayerId === 'player1'
              ? (deploymentStatus.player1 as any).placements
              : (deploymentStatus.player2 as any).placements;

          if (placementsData && placementsData.length > 0) {
            currentPlayerPlacementsCreatures = placementsData.map((p: any) => p.creature);
            console.log('[DeploymentDemo] FALLBACK: Using deployment status placements:', currentPlayerPlacementsCreatures.length);
          } else {
            console.warn('[DeploymentDemo] WARNING: No current player placements found in either source!');
          }
        } else {
          console.warn('[DeploymentDemo] WARNING: No deployment status available for fallback!');
        }

        // RACE CONDITION FIX: Also handle opponentPlacements being empty
        // This can happen when switching players in the same browser - socket sync may not complete in time
        // For PvE mode, we use a dedicated ref since socket sync is disabled
        let opponentCreatures: DeploymentCreature[] = [];
        const opponentId = currentPlayerId === 'player1' ? 'player2' : 'player1';

        if (opponentPlacements && opponentPlacements.length > 0) {
          // Preferred: Use synced opponent placements from React state
          opponentCreatures = opponentPlacements.map(p => p.creature);
          console.log('[DeploymentDemo] Using opponent placements from state:', opponentCreatures.length);
        } else if ((isChallenge || isAdventure) && pveEnemyPlacementsRef.current && pveEnemyPlacementsRef.current.length > 0) {
          // PVE/ADVENTURE FALLBACK: Use enemy placements stored in ref during challenge/adventure start
          // This is critical because socket sync is disabled in PvE/adventure mode
          opponentCreatures = pveEnemyPlacementsRef.current.map(p => p.creature);
          console.log('[DeploymentDemo] PVE/ADVENTURE FALLBACK: Using enemy placements from pveEnemyPlacementsRef:', opponentCreatures.length);
        } else if (lastSocketDataRef.current) {
          // PVP FALLBACK: Use raw socket data stored in ref
          // This is synchronously available even if React state hasn't updated yet
          const opponentPlacementsFromRef =
            opponentId === 'player1'
              ? lastSocketDataRef.current.player1Placements
              : lastSocketDataRef.current.player2Placements;

          if (opponentPlacementsFromRef && opponentPlacementsFromRef.length > 0) {
            opponentCreatures = opponentPlacementsFromRef.map(p => p.creature);
            console.log('[DeploymentDemo] FALLBACK: Using opponent placements from socket ref:', opponentCreatures.length);
          } else {
            console.warn('[DeploymentDemo] WARNING: No opponent placements in socket ref!');
          }
        } else {
          console.warn('[DeploymentDemo] WARNING: opponentPlacements empty and no fallback available!');
          console.warn('[DeploymentDemo] isChallenge:', isChallenge);
          console.warn('[DeploymentDemo] pveEnemyPlacementsRef.current:', pveEnemyPlacementsRef.current?.length || 0);
        }

        // CRITICAL: Only use DEPLOYED creatures (from placements), not roster arrays
        // The roster arrays (player1Creatures/player2Creatures) only have the CURRENT player's creatures
        // But we need creatures from BOTH players' deployments for combat rendering
        const allCreatures = [...currentPlayerPlacementsCreatures, ...opponentCreatures];

        // Deduplicate by creature ID (later entries override earlier ones)
        const creatureMap = new Map<string, DeploymentCreature>();
        allCreatures.forEach(c => creatureMap.set(c.id, c));
        const uniqueCreatures = Array.from(creatureMap.values());

        console.log('[DeploymentDemo] ===== LOADING CREATURE DATA INTO COMBAT =====');
        console.log('[DeploymentDemo] Total unique creatures:', uniqueCreatures.length);
        console.log('[DeploymentDemo] currentPlayerPlacementsCreatures count:', currentPlayerPlacementsCreatures.length);
        console.log('[DeploymentDemo] opponentCreatures count:', opponentCreatures.length);

        uniqueCreatures.forEach(creature => {
          console.log(`[DeploymentDemo] Creature "${creature.name}" (${creature.id}):`, {
            hasBattlefieldViews: !!creature.battlefieldDirectionalViews,
            eAttackFrames: creature.battlefieldDirectionalViews?.E?.attackFrames?.length ?? 0,
            neAttackFrames: creature.battlefieldDirectionalViews?.NE?.attackFrames?.length ?? 0,
            seAttackFrames: creature.battlefieldDirectionalViews?.SE?.attackFrames?.length ?? 0
          });
        });

        vizManager.setCreatureData(uniqueCreatures);

        // L4-KILL-005: Build creature lookup map for kill notifications
        // Adapts DeploymentCreature format to CreatureLookupDocument format
        const creatureLookup = new Map<string, CreatureLookupDocument>();
        uniqueCreatures.forEach(creature => {
          creatureLookup.set(creature.id, {
            name: creature.name,
            sprites: { menuSprite: creature.sprite }
          });
        });
        console.log('[DeploymentDemo] Built creature lookup for kill notifications:', creatureLookup.size, 'creatures');

        console.log('[DeploymentDemo] Starting visualization manager (with texture preloading)...');
        await vizManager.start();
        setCombatVisualizationManager(vizManager);
        console.log('[DeploymentDemo] ✓ Combat visualization manager started');

        // L4-SEAMLESS-001: Mark combat as ready to render
        setCombatInitPhase('ready');
        console.log('[DeploymentDemo] ✓ Combat initialization complete - ready to render');

        // Listen for combat state updates and feed to combat log
        // Use socketAdapter which works for both PvP (combatSocket) and PvE (pveSocket)
        socketAdapter.onCombatState((state: any) => {
          console.log('[DeploymentDemo] ===== COMBAT STATE UPDATE =====');
          console.log('[DeploymentDemo] Tick:', state.tick);
          console.log('[DeploymentDemo] Units:', state.units?.length || 0);
          console.log('[DeploymentDemo] Projectiles:', state.projectiles?.length || 0);
          console.log('[DeploymentDemo] Events:', state.events?.length || 0);

          // CRITICAL: Pass combat state to visualization manager for rendering
          if (vizManager) {
            vizManager.setState(state);
          }

          // L4-KILL-005: Process unit_died events for kill notifications
          if (state.events && state.events.length > 0) {
            const deathEvents = state.events.filter((e: any) => e.eventType === 'unit_died');
            deathEvents.forEach((event: any) => {
              killNotificationManagerRef.current.processDeathEvent(
                event,
                state,
                creatureLookup
              );
            });
          }

          setCombatState(state);
        });

        // Listen for combat completion
        // Use socketAdapter which works for both PvP (combatSocket) and PvE (pveSocket)
        socketAdapter.onCombatCompleted((result: any) => {
          console.log('[DeploymentDemo] ===== COMBAT COMPLETED =====');
          console.log('[DeploymentDemo] Winner:', result.winner || result.winnerId);
          console.log('[DeploymentDemo] Result:', result);

          // CRITICAL: Stop the visualization manager render loop to prevent infinite logging
          if (vizManager) {
            console.log('[DeploymentDemo] Stopping visualization manager render loop');
            vizManager.stop();
          }

          // L4-KILL-005: Clear kill notifications on combat end
          killNotificationManagerRef.current.clear();
          console.log('[DeploymentDemo] ✓ Kill notifications cleared');

          // Store the combat result for VictoryOverlay
          setCombatResult(result as CombatResult);
          setCombatState(result);
          // Note: combatActive stays true so the combat canvas remains visible behind the overlay
        });

        console.log('[DeploymentDemo] ✓ Combat initialization complete');

      } catch (error) {
        console.error('[DeploymentDemo] ✗ Failed to initialize combat visualization:', error);
        // L4-SEAMLESS-001: Mark combat init as failed
        setCombatInitPhase('failed');
        setCombatInitError(error instanceof Error ? error.message : 'Unknown error');
        if (renderer) renderer.destroy();
        if (vizManager) vizManager.destroy();
        // NOTE: Socket connection managed by early connection effect - don't leave here
      }
    };

    initializeCombat();

    // Cleanup only when combat ends or component unmounts
    return () => {
      console.log('[DeploymentDemo] Combat lifecycle: Cleanup triggered');
      isCleanedUp = true;

      if (vizManager) {
        console.log('[DeploymentDemo] Stopping and destroying visualization manager');
        vizManager.stop();
        vizManager.destroy();
      }
      if (renderer) {
        console.log('[DeploymentDemo] Destroying combat grid renderer');
        renderer.destroy();
      }

      // L4-KILL-005: Clear kill notifications on cleanup
      killNotificationManagerRef.current.clear();

      console.log('[DeploymentDemo] Removing combat event listeners');
      if (combatSocketRef.current) {
        // Disconnect the socket instead of removing listeners
        combatSocketRef.current.disconnect();
      }

      // PVE-FIX-007: Clear PvE adapter state callbacks to prevent stale callbacks on re-init
      if (pveSocketAdapterRef.current?.clearStateCallbacks) {
        pveSocketAdapterRef.current.clearStateCallbacks();
      }

      // NOTE: Socket connection managed by early connection effect - leave happens on component unmount

      setCombatVisualizationManager(null);
      setCombatGridRenderer(null);
    };
    // NOTE: combatCanvasSize intentionally NOT in deps - we capture size once when combat starts
    // Re-running this effect on viewport changes would destroy/recreate everything, causing race conditions
    // isChallenge/isAdventure is needed to determine which socket adapter to use (PvE vs PvP)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combatActive, matchId, isChallenge, isAdventure]);

  // L4-KILL-005: Dispose kill notification manager on component unmount
  useEffect(() => {
    return () => {
      killNotificationManagerRef.current.dispose();
    };
  }, []);

  const currentPlayerState = deploymentState[currentPlayerId];

  // Log placements whenever they change (for debugging disappearance bug)
  useEffect(() => {
    console.log(`[DeploymentDemo] currentPlayerState.placements changed:`, {
      playerId: currentPlayerId,
      count: currentPlayerState.placements.length,
      isLocked: currentPlayerState.isLocked,
      isReady: currentPlayerState.isReady,
      placements: currentPlayerState.placements.map(p => ({
        creatureId: p.creature.id,
        creatureName: p.creature.name,
        hex: p.hex
      }))
    });
  }, [currentPlayerState.placements, currentPlayerId, currentPlayerState.isLocked, currentPlayerState.isReady]);

  // Sync local lock state with server
  useEffect(() => {
    if (deploymentStatus?.isLocked && !currentPlayerState.isLocked) {
      console.log(`[DeploymentDemo] Syncing lock state - calling markLocked(${currentPlayerId})`);
      console.log(`[DeploymentDemo] Current placements before lock:`, currentPlayerState.placements.length);
      // Just update the locked flag, DON'T clear placements
      markLocked(currentPlayerId);
    }
  }, [deploymentStatus?.isLocked, currentPlayerState.isLocked, currentPlayerId, markLocked, currentPlayerState.placements]);

  // Clear repositionCreatureId when deployment becomes locked
  useEffect(() => {
    if (deploymentStatus?.isLocked && repositionCreatureId) {
      console.log('[DeploymentDemo] Deployment locked - clearing repositionCreatureId');
      setRepositionCreatureId(null);
    }
  }, [deploymentStatus?.isLocked, repositionCreatureId]);

  // CRITICAL FIX: Add canvas to DOM after React re-renders combat viewer
  useEffect(() => {
    console.log('[DeploymentDemo] Canvas attachment useEffect triggered:', {
      hasCombatGridRenderer: !!combatGridRenderer,
      hasCombatVisualizationManager: !!combatVisualizationManager,
      hasCanvasRef: !!combatCanvasRef.current
    });

    if (combatGridRenderer && combatVisualizationManager && combatCanvasRef.current) {
      const canvas = combatGridRenderer.getCanvas();
      console.log('[DeploymentDemo] useEffect: Adding canvas to DOM after React render');
      console.log('[DeploymentDemo] DEBUG: Canvas parentElement before:', canvas.parentElement);
      console.log('[DeploymentDemo] DEBUG: combatCanvasRef.current:', combatCanvasRef.current);

      // Clear any existing content and add canvas
      combatCanvasRef.current.innerHTML = '';
      combatCanvasRef.current.appendChild(canvas);

      console.log('[DeploymentDemo] DEBUG: Canvas parentElement after:', canvas.parentElement);
      console.log('[DeploymentDemo] ✓ Canvas successfully added to DOM after React render');
    }
  }, [combatGridRenderer, combatVisualizationManager]);

  // Setup camera controls for combat view (L4-ZOOM-005)
  useEffect(() => {
    if (!combatGridRenderer || !combatVisualizationManager || !combatCanvasRef.current) return;

    const canvas = combatGridRenderer.getCanvas();
    const cameraManager = combatGridRenderer.getCameraManager();

    if (!cameraManager || !canvas) return;

    console.log('[DeploymentDemo] Setting up combat view camera controls');

    // Center camera on grid content for all devices
    // Uses fitToContent to properly center and zoom to show entire battlefield
    const gridOffset = combatGridRenderer.getGridOffset();
    const gridBounds = combatGridRenderer.getGridBounds();

    // Content bounds in world coordinates
    const contentBounds = {
      x: gridOffset.x + gridBounds.minX,
      y: gridOffset.y + gridBounds.minY,
      width: gridBounds.width,
      height: gridBounds.height
    };

    // Use smaller margin on desktop (5%) vs mobile (10%) since screens are larger
    const margin = viewport.isMobile ? 0.1 : 0.05;
    // Slight vertical bias to show more of the lower battlefield (where action happens)
    const verticalBias = 0.05;

    console.log('[DeploymentDemo] Camera fitToContent:', {
      contentBounds,
      canvasSize: combatCanvasSize,
      margin,
      verticalBias,
      isMobile: viewport.isMobile
    });

    cameraManager.fitToContent(
      contentBounds,
      combatCanvasSize.width,
      combatCanvasSize.height,
      margin,
      verticalBias
    );

    console.log('[DeploymentDemo] Camera state after centering:', cameraManager.getState());

    // Setup wheel zoom handler
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      // Get cursor position relative to canvas
      const rect = canvas.getBoundingClientRect();
      const cursorPos = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      // Calculate new zoom level
      const currentZoom = cameraManager.getZoom();
      const config = cameraManager.getConfig();
      const targetZoom = calculateWheelZoom(event.deltaY, currentZoom, config.wheelZoomFactor);

      // Apply zoom at cursor position
      combatGridRenderer.zoomAtPoint(targetZoom, cursorPos);
    };

    // Setup WASD keyboard pan handler
    const panHandler = createKeyboardPanHandler(cameraManager);
    combatKeyboardPanRef.current = panHandler;

    // Setup pinch-to-zoom handler for mobile
    const pinchHandler = createPinchZoomHandler(cameraManager, canvas);
    combatPinchZoomRef.current = pinchHandler;

    // Animation loop for continuous pan updates
    const updateLoop = () => {
      if (combatKeyboardPanRef.current) {
        combatKeyboardPanRef.current.update();
        cameraManager.applyTransform();
      }
      combatAnimationFrameRef.current = requestAnimationFrame(updateLoop);
    };

    // Add event listeners
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('touchstart', pinchHandler.handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', pinchHandler.handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', pinchHandler.handleTouchEnd);
    window.addEventListener('keydown', panHandler.handleKeyDown);
    window.addEventListener('keyup', panHandler.handleKeyUp);

    // Start update loop
    combatAnimationFrameRef.current = requestAnimationFrame(updateLoop);

    console.log('[DeploymentDemo] ✓ Combat view camera controls initialized (wheel, pinch, keyboard)');

    // Cleanup
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('touchstart', pinchHandler.handleTouchStart);
      canvas.removeEventListener('touchmove', pinchHandler.handleTouchMove);
      canvas.removeEventListener('touchend', pinchHandler.handleTouchEnd);
      window.removeEventListener('keydown', panHandler.handleKeyDown);
      window.removeEventListener('keyup', panHandler.handleKeyUp);

      if (combatAnimationFrameRef.current) {
        cancelAnimationFrame(combatAnimationFrameRef.current);
      }

      if (combatKeyboardPanRef.current) {
        combatKeyboardPanRef.current.destroy();
        combatKeyboardPanRef.current = null;
      }

      if (combatPinchZoomRef.current) {
        combatPinchZoomRef.current.destroy();
        combatPinchZoomRef.current = null;
      }
    };
  }, [combatGridRenderer, combatVisualizationManager]);

  // Handle canvas resize on orientation change during combat (mobile)
  useEffect(() => {
    if (!combatGridRenderer || !combatActive || !viewport.isMobile) return;

    // Resize the canvas to match new viewport dimensions
    combatGridRenderer.resize(combatCanvasSize.width, combatCanvasSize.height);

    // Re-center camera on grid content after resize
    const cameraManager = combatGridRenderer.getCameraManager();
    if (cameraManager) {
      const gridOffset = combatGridRenderer.getGridOffset();
      const gridBounds = combatGridRenderer.getGridBounds();

      const contentBounds = {
        x: gridOffset.x + gridBounds.minX,
        y: gridOffset.y + gridBounds.minY,
        width: gridBounds.width,
        height: gridBounds.height,
      };

      cameraManager.fitToContent(
        contentBounds,
        combatCanvasSize.width,
        combatCanvasSize.height,
        0.1, // 10% margin for mobile
        0.05 // Slight vertical bias
      );
    }
  }, [combatGridRenderer, combatActive, viewport.isMobile, combatCanvasSize.width, combatCanvasSize.height]);

  // Combat view zoom button handlers
  const handleCombatZoomIn = useCallback(() => {
    if (combatGridRenderer) {
      combatGridRenderer.zoomIn();
    }
  }, [combatGridRenderer]);

  const handleCombatZoomOut = useCallback(() => {
    if (combatGridRenderer) {
      combatGridRenderer.zoomOut();
    }
  }, [combatGridRenderer]);

  // L4-TAP-004: Pan camera to a creature when its card is clicked in the TeamAbilityPanel
  const handleCreaturePanTo = useCallback((unitId: string, position: AxialCoordinate) => {
    if (!combatGridRenderer) return;
    const cameraManager = combatGridRenderer.getCameraManager();
    if (!cameraManager) return;

    const worldPos = combatGridRenderer.hexToPixel(position);
    const zoom = cameraManager.getZoom();
    const panX = combatCanvasSize.width / 2 - worldPos.x * zoom;
    const panY = combatCanvasSize.height / 2 - worldPos.y * zoom;

    cameraManager.setPan(panX, panY);
    cameraManager.applyTransform();
  }, [combatGridRenderer, combatCanvasSize]);

  // Handle return to lobby from victory overlay (Leave Match)
  const handleReturnToLobby = useCallback(() => {
    console.log('[DeploymentDemo] Leaving match and returning to lobby...');
    // Notify server we're leaving the match series
    emitLeaveMatch();
    // Clear combat state
    setCombatResult(null);
    combatActiveRef.current = false;
    setCombatActive(false);
    setCombatState(null);
    setSeriesRecord(null);
    setRoundNumber(1);
    // Navigate to home/lobby page
    navigate('/home');
  }, [navigate, emitLeaveMatch]);

  // Handle Fight Again request
  const handleFightAgain = useCallback(() => {
    console.log('[DeploymentDemo] Fight Again requested...');
    // Request rematch from server - this will trigger onRoundReset
    emitRequestRematch();
  }, [emitRequestRematch]);

  // Handle VS screen completion - transition to combat
  // L4-SEAMLESS-002: VS screen complete only dismisses overlay
  // Combat is already active and initializing behind the VS screen
  const handleVSScreenComplete = useCallback(() => {
    console.log('[DeploymentDemo] VS screen complete, revealing combat (already initialized)');
    setShowVSScreen(false);
    // NOTE: setCombatActive already called in onCombatStarted - combat is running behind VS screen
    console.log('[DeploymentDemo] ✓ VS screen dismissed, combat visible');
  }, []);

  // Handle drag start from roster
  const handleRosterDragStart = useCallback((creature: DeploymentCreature, _event: React.DragEvent | React.TouchEvent) => {
    console.log('Drag started:', creature.name);
    startDrag(creature);
  }, [startDrag]);

  // Handle hex hover (update drag target)
  const handleHexHover = useCallback((hex: AxialCoordinate | null) => {
    if (dragState.phase === 'dragging') {
      console.log('[HexHover] During drag, updating target to:', hex ? `{q: ${hex.q}, r: ${hex.r}}` : 'null');
      updateDrag(hex);
    }
  }, [dragState.phase, updateDrag]);

  // Handle drop on grid - MUST BE DEFINED BEFORE handleRosterDragEnd
  const handleDrop = useCallback((hex: AxialCoordinate) => {
    console.log('Drop on hex:', `{q: ${hex.q}, r: ${hex.r}}`);
    console.log('Current player ID:', currentPlayerId);
    console.log('Drag state:', { isValid: dragState.isValid, creature: dragState.creature?.name, creaturePlayerId: dragState.creature?.playerId });

    // Check if socket is connected before allowing placement (skip in challenge/adventure mode)
    if (!isChallenge && !isAdventure && !isConnected) {
      console.warn('[DeploymentDemo] Cannot place creature - socket not connected');
      cancelDrag();
      return;
    }

    const success = endDrag(hex);
    console.log('Placement success:', success);

    // Emit placement to server if successful (skip in challenge/adventure mode - no opponent sync needed)
    if (success && dragState.creature && !isChallenge && !isAdventure) {
      const placement: CreaturePlacement = {
        hex,
        creature: dragState.creature
      };
      console.log('[DeploymentDemo] Emitting placement to server:', {
        creatureName: placement.creature.name,
        hex: placement.hex,
        hasBattlefieldViews: !!placement.creature.battlefieldDirectionalViews,
        viewKeys: placement.creature.battlefieldDirectionalViews ? Object.keys(placement.creature.battlefieldDirectionalViews) : [],
        eAttackFrames: placement.creature.battlefieldDirectionalViews?.E?.attackFrames?.length ?? 0,
        neAttackFrames: placement.creature.battlefieldDirectionalViews?.NE?.attackFrames?.length ?? 0,
        seAttackFrames: placement.creature.battlefieldDirectionalViews?.SE?.attackFrames?.length ?? 0
      });
      emitPlacement(placement);
    }
  }, [endDrag, dragState, emitPlacement, currentPlayerId, isConnected, cancelDrag]);

  // Handle drag end from roster - Uses handleDrop, so must come AFTER
  const handleRosterDragEnd = useCallback((_event: React.DragEvent | React.TouchEvent) => {
    console.log('Drag ended from roster');

    // If drag is no longer active, it was already handled (prevents race condition)
    if (dragState.phase === 'idle') {
      console.log('Drag already completed, skipping');
      return;
    }

    // Complete the drag operation if we have a valid target
    if (dragState.phase === 'dragging' && dragState.targetHex && dragState.isValid) {
      console.log('Auto-completing drag to target hex:', dragState.targetHex);
      handleDrop(dragState.targetHex);
    } else {
      console.log('Cancelling invalid drag');
      cancelDrag();
    }
  }, [dragState.phase, dragState.targetHex, dragState.isValid, handleDrop, cancelDrag]);

  // Mobile tap-to-select handler (TASK-TDI-3)
  const handleCreatureSelect = useCallback((creature: any) => {
    console.log('[Gallery] Creature selected (tap-to-select):', creature.name);
    setSelectedCreatureId(creature.id);
    setSelectedCreature(creature);
  }, []);

  // Clear selection helper
  const clearSelection = useCallback(() => {
    setSelectedCreatureId(null);
    setSelectedCreature(null);
  }, []);

  // Gallery-based deployment handlers
  const handleGalleryDragStart = useCallback((creature: any) => {
    console.log('[Gallery] Drag started:', creature.name);
    setDraggedGalleryCreature(creature);

    // Clear any tap-to-select selection when drag starts (TASK-TDI-3)
    clearSelection();

    // Transform gallery creature to deployment creature format
    const deploymentCreature = transformToDeploymentCreature(creature, currentPlayerId);
    startDrag(deploymentCreature);
  }, [startDrag, currentPlayerId, clearSelection]);

  const handleGalleryDragEnd = useCallback(() => {
    console.log('[Gallery] Drag ended');
    setDraggedGalleryCreature(null);

    // Same logic as roster drag end
    if (dragState.phase === 'idle') {
      return;
    }

    if (dragState.phase === 'dragging' && dragState.targetHex && dragState.isValid) {
      handleDrop(dragState.targetHex);
    } else {
      cancelDrag();
    }
  }, [dragState.phase, dragState.targetHex, dragState.isValid, handleDrop, cancelDrag]);

  // Get deployed creature IDs for gallery
  const deployedCreatureIds = React.useMemo(() => {
    const ids = new Set<string>();
    currentPlayerState.placements.forEach(placement => {
      // Extract original creature ID if it exists
      if (placement.creature.id) {
        ids.add(placement.creature.id);
      }
    });
    return ids;
  }, [currentPlayerState.placements]);

  // Handle hex click (for moving existing creatures OR tap-to-place mobile)
  const handleHexClick = useCallback((hex: AxialCoordinate) => {
    console.log('Hex clicked:', hex);

    // PATH 1: If we're dragging, treat as drop (desktop drag-drop - UNCHANGED)
    if (dragState.phase === 'dragging') {
      handleDrop(hex);
      return;
    }

    // PATH 2: Tap-to-reposition - if a placed creature is selected for repositioning
    if (repositionCreatureId) {
      console.log('[Reposition] Attempting to reposition creature:', repositionCreatureId, 'to hex:', hex);

      // Check if socket is connected (skip in challenge/adventure mode)
      if (!isChallenge && !isAdventure && !isConnected) {
        console.warn('[Reposition] Cannot reposition - socket not connected');
        setRepositionCreatureId(null);
        return;
      }

      // Check if deployment is locked
      if (deploymentStatus?.isLocked) {
        console.warn('[Reposition] Cannot reposition - deployment is locked');
        setRepositionCreatureId(null);
        return;
      }

      // Find the creature being repositioned
      const sourcePlacement = currentPlayerState.placements.find(
        p => p.creature.id === repositionCreatureId
      );

      if (!sourcePlacement) {
        console.warn('[Reposition] Source creature not found:', repositionCreatureId);
        setRepositionCreatureId(null);
        return;
      }

      // Check if tapping the same hex (where creature currently is) - deselect
      if (sourcePlacement.hex.q === hex.q && sourcePlacement.hex.r === hex.r) {
        console.log('[Reposition] Tapped same hex, deselecting creature');
        setRepositionCreatureId(null);
        return;
      }

      // Check if tapping on another creature - switch selection to that creature
      const targetPlacement = currentPlayerState.placements.find(
        p => p.hex.q === hex.q && p.hex.r === hex.r
      );

      if (targetPlacement && targetPlacement.creature.playerId === currentPlayerId) {
        console.log('[Reposition] Tapped another creature, switching selection to:', targetPlacement.creature.id);
        setRepositionCreatureId(targetPlacement.creature.id);
        return;
      }

      // Validate the target hex (must be in player's deployment zone, not occupied)
      const validation = validatePlacement(hex, sourcePlacement.creature, currentPlayerId);
      console.log('[Reposition] Validation result:', validation);

      if (!validation.valid) {
        console.warn('[Reposition] Invalid target hex:', validation.message);
        // Keep selection - don't clear on invalid tap (user may want to try another hex)
        return;
      }

      // Perform the reposition: remove from old position, place at new position
      console.log('[Reposition] Moving creature from', sourcePlacement.hex, 'to', hex);

      // Remove from old position
      const removed = removeCreature(repositionCreatureId);
      if (!removed) {
        console.error('[Reposition] Failed to remove creature from old position');
        setRepositionCreatureId(null);
        return;
      }

      // Place at new position
      const placed = placeCreature(sourcePlacement.creature, hex);
      if (!placed) {
        console.error('[Reposition] Failed to place creature at new position');
        // Try to restore at old position
        placeCreature(sourcePlacement.creature, sourcePlacement.hex);
        setRepositionCreatureId(null);
        return;
      }

      // Emit the change to server (removal + placement) - skip in challenge/adventure mode
      if (!isChallenge && !isAdventure) {
        emitRemoval(repositionCreatureId);
        emitPlacement({
          hex,
          creature: sourcePlacement.creature
        });
      }

      console.log('[Reposition] Successfully repositioned creature');
      setRepositionCreatureId(null);
      return;
    }

    // PATH 3: Mobile tap-to-place from gallery - if creature is selected from roster
    if (selectedCreature) {
      console.log('[TapToPlace] Attempting to place selected creature:', selectedCreature.name);

      // Check if socket is connected before allowing placement (skip in challenge/adventure mode)
      if (!isChallenge && !isAdventure && !isConnected) {
        console.warn('[TapToPlace] Cannot place creature - socket not connected');
        clearSelection();
        return;
      }

      // Check if max deployment reached
      if (getPlacementCount(currentPlayerId) >= creatureLimit) {
        console.warn('[TapToPlace] Cannot place creature - max deployment reached');
        clearSelection();
        return;
      }

      // Transform gallery creature to deployment creature format
      const deploymentCreature = transformToDeploymentCreature(selectedCreature, currentPlayerId);

      // FIXED: Bypass drag state machine for tap-to-place (React state updates are async)
      // Directly validate and place instead of startDrag → updateDrag → endDrag
      const validation = validatePlacement(hex, deploymentCreature, currentPlayerId);
      console.log('[TapToPlace] Validation result:', validation);

      if (!validation.valid) {
        console.warn('[TapToPlace] Invalid placement:', validation.message);
        clearSelection();
        return;
      }

      // Place the creature directly
      const success = placeCreature(deploymentCreature, hex);
      console.log('[TapToPlace] Placement success:', success);

      // Emit placement to server if successful (skip in challenge/adventure mode)
      if (success && !isChallenge && !isAdventure) {
        const placement: CreaturePlacement = {
          hex,
          creature: deploymentCreature
        };
        console.log('[TapToPlace] Emitting placement to server:', placement.creature.name);
        emitPlacement(placement);
      }

      // Clear selection after placement attempt
      clearSelection();
      return;
    }

    // PATH 4: Otherwise, check if there's a creature here to pick up (legacy reposition via hex click)
    const creature = currentPlayerState.placements.find(
      p => p.hex.q === hex.q && p.hex.r === hex.r
    )?.creature;

    if (creature && creature.playerId === currentPlayerId) {
      // Select this creature for repositioning (tap-to-select behavior)
      setRepositionCreatureId(creature.id);
      console.log('[Reposition] Selected creature via hex click:', creature.id);
    }
  }, [dragState.phase, currentPlayerState.placements, currentPlayerId, handleDrop, startDrag, validatePlacement, placeCreature, removeCreature, selectedCreature, isConnected, getPlacementCount, emitPlacement, clearSelection, repositionCreatureId, deploymentStatus?.isLocked, emitRemoval, creatureLimit]);

  // Handle creature sprite click (for repositioning via tap-to-select-then-tap-to-place)
  const handleCreatureClick = useCallback((creatureId: string, hex: AxialCoordinate) => {
    console.log('Creature clicked:', creatureId, 'at hex:', hex);

    // Don't allow selection if deployment is locked
    if (deploymentStatus?.isLocked) {
      console.log('[Reposition] Deployment is locked, ignoring click');
      return;
    }

    // Don't allow if dragging from gallery
    if (dragState.phase === 'dragging') {
      console.log('[Reposition] Drag in progress, ignoring click');
      return;
    }

    // Find the creature by ID
    const placement = currentPlayerState.placements.find(
      p => p.creature.id === creatureId
    );

    if (!placement || placement.creature.playerId !== currentPlayerId) {
      console.log('[Reposition] Creature not found or not owned by current player');
      return;
    }

    // Toggle selection for tap-to-select-then-tap-to-place repositioning
    if (repositionCreatureId === creatureId) {
      // Same creature - deselect (cancel repositioning)
      setRepositionCreatureId(null);
      console.log('[Reposition] Deselected creature:', creatureId);
    } else {
      // Different creature or no selection - select this one for repositioning
      setRepositionCreatureId(creatureId);
      // Clear gallery selection if any (can't select from gallery and reposition at same time)
      if (selectedCreatureId) {
        clearSelection();
      }
      console.log('[Reposition] Selected creature for repositioning:', creatureId);
    }
  }, [deploymentStatus?.isLocked, dragState.phase, currentPlayerState.placements, currentPlayerId, repositionCreatureId, selectedCreatureId, clearSelection]);

  // Handle creature right-click (for removal on desktop)
  const handleCreatureRightClick = useCallback((creatureId: string, hex: AxialCoordinate) => {
    console.log('[DeploymentDemo] Right-click removal:', creatureId, 'at hex:', hex);

    // Don't allow removal if deployment is locked
    if (deploymentStatus?.isLocked) {
      console.warn('[DeploymentDemo] Cannot remove creature - deployment is locked');
      return;
    }

    // Don't allow removal during active drag
    if (dragState.phase === 'dragging') {
      console.warn('[DeploymentDemo] Cannot remove creature - drag in progress');
      return;
    }

    // Don't allow removal if socket not connected (skip in challenge/adventure mode)
    if (!isChallenge && !isAdventure && !isConnected) {
      console.warn('[DeploymentDemo] Cannot remove creature - socket not connected');
      return;
    }

    // Remove from local state
    const success = removeCreature(creatureId);

    if (success && !isChallenge && !isAdventure) {
      // Emit removal to server for opponent sync (skip in challenge/adventure mode)
      console.log('[DeploymentDemo] Emitting removal to server:', creatureId);
      emitRemoval(creatureId);
    }
  }, [deploymentStatus?.isLocked, dragState.phase, isConnected, removeCreature, emitRemoval, isChallenge, isAdventure]);

  // Mobile tap-and-hold removal handlers
  // Note: Haptic feedback is handled in the renderer's gesture controller

  // Handle mobile hold detected (500ms threshold passed)
  const handleRemovalHoldDetected = useCallback((creatureId: string, hex: AxialCoordinate) => {
    console.log('[DeploymentDemo] Mobile removal hold detected:', creatureId, 'at hex:', hex);

    // Don't show animation if locked (renderer handles visuals, but we can log)
    if (deploymentStatus?.isLocked) {
      console.warn('[DeploymentDemo] Hold detected but deployment is locked');
    }
  }, [deploymentStatus?.isLocked]);

  // Handle removal progress (0-1, for optional UI feedback)
  const handleRemovalProgress = useCallback((_progress: number) => {
    // Renderer handles the timer circle animation
    // Could add additional UI feedback here if needed (e.g., screen tint)
  }, []);

  // Handle mobile removal complete (3 second hold finished)
  const handleRemovalComplete = useCallback((creatureId: string, hex: AxialCoordinate) => {
    console.log('[DeploymentDemo] Mobile removal complete:', creatureId, 'at hex:', hex);

    // Don't allow removal if deployment is locked
    if (deploymentStatus?.isLocked) {
      console.warn('[DeploymentDemo] Cannot remove creature - deployment is locked');
      return;
    }

    // Don't allow removal during active drag
    if (dragState.phase === 'dragging') {
      console.warn('[DeploymentDemo] Cannot remove creature - drag in progress');
      return;
    }

    // Don't allow removal if socket not connected (skip in challenge/adventure mode)
    if (!isChallenge && !isAdventure && !isConnected) {
      console.warn('[DeploymentDemo] Cannot remove creature - socket not connected');
      return;
    }

    // Remove from local state
    const success = removeCreature(creatureId);

    if (success && !isChallenge && !isAdventure) {
      // Emit removal to server for opponent sync (skip in challenge/adventure mode)
      console.log('[DeploymentDemo] Emitting mobile removal to server:', creatureId);
      emitRemoval(creatureId);
    }
  }, [deploymentStatus?.isLocked, dragState.phase, isConnected, removeCreature, emitRemoval, isChallenge, isAdventure]);

  // Handle removal cancelled (released early or moved finger)
  const handleRemovalCancelled = useCallback(() => {
    console.log('[DeploymentDemo] Mobile removal cancelled');
    // Haptic feedback handled in renderer
  }, []);

  // Handle ESC key to cancel drag, clear gallery selection, or clear reposition selection (TASK-TDI-6)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dragState.phase === 'dragging') {
          console.log('Drag cancelled');
          cancelDrag();
        } else if (repositionCreatureId) {
          console.log('[Reposition] Selection cleared via ESC');
          setRepositionCreatureId(null);
        } else if (selectedCreatureId) {
          console.log('Gallery selection cleared');
          clearSelection();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dragState.phase, cancelDrag, selectedCreatureId, clearSelection, repositionCreatureId]);

  // Dev tool: Shift+B toggles background position editor
  const bgEditorRef = useRef<BackgroundPositionEditor | null>(null);
  useEffect(() => {
    const handleBgEditorKey = (e: KeyboardEvent) => {
      if (e.key === 'B' && e.shiftKey) {
        if (bgEditorRef.current) {
          bgEditorRef.current.destroy();
          bgEditorRef.current = null;
          return;
        }

        // Use whichever renderer is currently active (combat or deployment)
        const activeRenderer: HexGridRenderer | null =
          (combatGridRenderer && 'getTerrainRenderer' in combatGridRenderer)
            ? combatGridRenderer as CombatGridRenderer
            : deploymentRendererRef.current;

        if (!activeRenderer) return;
        const gameplayContainers = activeRenderer.getGameplayContainers();
        if (gameplayContainers.length === 0) return;

        const terrainRenderer = activeRenderer.getTerrainRenderer();
        const bgSprite = terrainRenderer?.getNearBackgroundSprite() ?? null;
        const canvas = activeRenderer.getCanvas();
        bgEditorRef.current = new BackgroundPositionEditor(gameplayContainers, bgSprite, canvas);
      }
    };

    window.addEventListener('keydown', handleBgEditorKey);
    return () => {
      window.removeEventListener('keydown', handleBgEditorKey);
      if (bgEditorRef.current) {
        bgEditorRef.current.destroy();
        bgEditorRef.current = null;
      }
    };
  }, [combatGridRenderer]);

  const handleClearAll = () => {
    clearAllPlacements(currentPlayerId);
  };

  // Challenge mode: Start PvE battle directly
  // Challenge battle result state
  const [challengeResult, setChallengeResult] = useState<{
    victory: boolean;
    stars: number;
    turnsUsed: number;
    unitsLost: number;
    encounterName: string;
    damageDealt?: number;
    damageTaken?: number;
    enemiesKilled?: number;
    rewards?: Array<{
      type: string;
      id: string;
      description?: string;
    }>;
  } | null>(null);

  const handleChallengeStart = useCallback(async () => {
    if (!challengeWeekId || !challengeDay || !challengeAttemptId) {
      console.error('[DeploymentDemo] Missing challenge parameters');
      return;
    }

    console.log('[DeploymentDemo] Starting challenge battle...');
    markReady(currentPlayerId);

    // Prepare placements data
    const playerPlacements = currentPlayerState.placements.map(p => ({
      creatureId: p.creature.id,
      position: p.hex,
      creature: p.creature
    }));

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      // Step 1: Create PvE match via REST API
      const response = await fetch(
        `${apiUrl}/api/challenges/${challengeWeekId}/days/${challengeDay}/battle`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await getIdToken()}`,
          },
          body: JSON.stringify({
            attemptId: challengeAttemptId,
            placements: playerPlacements
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create challenge battle');
      }

      const data = await response.json();
      console.log('[DeploymentDemo] PvE match created:', data);

      // Initialize wave state from wave schedule (if scenario-based battle)
      if (data.waveSchedule) {
        const schedule = data.waveSchedule;
        waveScheduleRef.current = schedule;
        setWaveState({
          currentWave: 1,
          totalWaves: schedule.totalWaves,
          enemiesRemaining: schedule.waves[0]?.enemyCount || 0,
          totalEnemiesInWave: schedule.waves[0]?.enemyCount || 0,
          nextWaveDelay: schedule.waves.length > 1 ? schedule.waves[1].delay : null,
          isSpawning: false,
        });
        console.log('[DeploymentDemo] Wave schedule loaded:', schedule);
      }

      // Step 2: Connect to PvE socket with the matchId
      if (data.matchId) {
        console.log('[DeploymentDemo] Connecting to PvE socket, matchId:', data.matchId);

        // Convert enemy preview to opponentPlacements for VS screen display
        // The enemyPreview from the API contains positioned enemies with sprite data
        if (data.enemyPreview && Array.isArray(data.enemyPreview)) {
          const enemyPlacements = data.enemyPreview.map((enemy: any) => ({
            creature: {
              // FIX: Use creatureId (creature document ID) as the key, NOT instanceId (UUID)
              // This matches the combat engine's unit.creatureId, enabling wave-spawned units
              // to find their sprite data since they share the same creatureId as initial enemies
              id: enemy.creatureId || enemy.instanceId,
              name: enemy.name,
              sprite: enemy.sprite || '', // Menu sprite for VS screen
              playerId: 'player2' as const,
              // Include directional sprites for combat rendering
              battlefieldDirectionalViews: enemy.battlefieldDirectionalViews,
              size: enemy.size || 1
            },
            hex: enemy.position
          }));

          // RACE CONDITION FIX: Store in ref FIRST for synchronous access during combat init
          // React setState is async, so the ref ensures data is available immediately
          pveEnemyPlacementsRef.current = enemyPlacements;
          console.log('[DeploymentDemo] Stored enemy placements in ref:', enemyPlacements.length);

          // Then update React state for UI rendering
          setOpponentPlacements(enemyPlacements);

          // CRITICAL: Signal ready to backend AFTER enemy data is stored
          // This ensures backend doesn't start combat until frontend has all sprite data loaded
          console.log('[DeploymentDemo] Enemy data loaded, will signal ready after socket connection');

          // DEBUG: Log enemy sprite data lengths to verify transmission
          enemyPlacements.forEach((ep, idx) => {
            const bdv = ep.creature.battlefieldDirectionalViews;
            console.log(`[DeploymentDemo] SPRITE DATA CHECK - Enemy ${idx} "${ep.creature.name}":`, {
              hasSprite: !!ep.creature.sprite,
              spriteLength: ep.creature.sprite?.length || 0,
              hasBattlefieldViews: !!bdv,
              E_idleFrames: bdv?.E?.idleFrames?.length || 0,
              E_idle0Length: bdv?.E?.idleFrames?.[0]?.length || 0,
              E_attackFrames: bdv?.E?.attackFrames?.length || 0,
              NE_idleFrames: bdv?.NE?.idleFrames?.length || 0,
              SE_idleFrames: bdv?.SE?.idleFrames?.length || 0,
            });
            // Log a sample of the first frame to see if it's truncated
            if (bdv?.E?.idleFrames?.[0]) {
              const frame0 = bdv.E.idleFrames[0];
              console.log(`[DeploymentDemo] E.idleFrames[0] preview:`, {
                length: frame0.length,
                first100: frame0.substring(0, 100),
                last100: frame0.substring(Math.max(0, frame0.length - 100))
              });
            }
          });
          console.log('[DeploymentDemo] Set opponent placements from enemy preview:', enemyPlacements.length);
        }

        // Show VS screen while connecting
        setShowVSScreen(true);
        setVsLoadingProgress(25);

        // Connect to PvE socket
        const pveState = await pveSocket.connect(data.matchId);
        console.log('[DeploymentDemo] Connected to PvE match:', pveState);

        setVsLoadingProgress(50);

        // =====================================================================
        // FIX: Correct event ordering to prevent race conditions
        // Order: 1) Clear listeners, 2) Create buffers, 3) Register listeners,
        //        4) Create adapter, 5) THEN signal ready to backend
        // =====================================================================

        // Step 1: Clear any stale listeners from previous sessions
        pveSocket.clearCombatListeners();
        console.log('[DeploymentDemo] Cleared stale combat listeners');

        // Step 2: Create buffers BEFORE registering listeners
        // This ensures no states are lost between listener registration and buffer creation
        const stateBuffer: any[] = [];
        const stateCallbacks: Array<(state: any) => void> = [];

        // Step 3: Register combat-started listener FIRST (before markReady)
        pveSocket.onCombatStarted(({ matchId: startedMatchId }) => {
          console.log('[DeploymentDemo] PvE combat started:', startedMatchId);
          setVsLoadingProgress(75);
        });

        // Step 4: Register state listener immediately to capture tick 0
        pveSocket.onCombatState((state) => {
          // Log initial state capture for debugging
          if (state.tick === 0) {
            console.log('[DeploymentDemo] 🎯 Received tick 0 (initial state) with', state.units?.length || 0, 'units');
          }

          if (stateCallbacks.length > 0) {
            // Callbacks registered, forward to ALL of them
            console.log('[DeploymentDemo] PvE adapter: forwarding state tick', state.tick, 'to', stateCallbacks.length, 'callbacks');
            for (const callback of stateCallbacks) {
              callback(state);
            }
          } else {
            // Callbacks not yet registered, buffer the state
            console.log('[DeploymentDemo] PvE adapter: buffering state tick', state.tick);
            stateBuffer.push(state);
          }
        });

        const pveAdapter = {
          onCombatState: (callback: (state: any) => void) => {
            console.log('[DeploymentDemo] PvE adapter: registering state callback #' + (stateCallbacks.length + 1) + ', buffered states:', stateBuffer.length);
            stateCallbacks.push(callback);

            // Replay any buffered states to this new callback
            if (stateBuffer.length > 0) {
              console.log('[DeploymentDemo] PvE adapter: replaying', stateBuffer.length, 'buffered states to new callback');
              for (const state of stateBuffer) {
                console.log('[DeploymentDemo] PvE adapter: replaying state tick', state.tick);
                callback(state);
              }
              // Only clear buffer after FIRST callback gets replay
              // Subsequent callbacks don't need replay (states will flow through stateCallbacks)
              if (stateCallbacks.length === 1) {
                stateBuffer.length = 0;
              }
            }
          },
          onCombatCompleted: (callback: (result: any) => void) => {
            console.log('[DeploymentDemo] PvE adapter: registering completion callback');
            pveSocket.onCompleted((result) => {
              console.log('[DeploymentDemo] PvE adapter: received completion');
              // Convert PvE result format to standard combat result
              const combatResult = {
                winnerId: result.outcome === 'victory' ? currentPlayerId : 'player2',
                reason: result.reason,
                stats: result.stats,
                matchId: data.matchId
              };
              callback(combatResult);
            });
          },
          onError: (callback: (error: Error) => void) => {
            console.log('[DeploymentDemo] PvE adapter: registering error callback');
            pveSocket.onError((error) => {
              console.error('[DeploymentDemo] PvE adapter: received error');
              callback(new Error(error.message));
            });
          },
          signalReady: () => {
            console.log('[DeploymentDemo] PvE adapter: signaling ready (no-op for PvE)');
            // PvE doesn't need explicit ready signal - combat starts automatically
          },
          // PvE doesn't use effect frames (yet), provide no-op handlers
          onEffectFrames: (_callback: (data: any) => void) => {
            console.log('[DeploymentDemo] PvE adapter: onEffectFrames (no-op)');
          },
          requestEffectFrames: () => {
            console.log('[DeploymentDemo] PvE adapter: requestEffectFrames (no-op)');
          },
          // FIX: Wire up combat events for damage numbers, health bars, etc.
          onCombatEvents: (callback: (events: any[]) => void) => {
            console.log('[DeploymentDemo] PvE adapter: registering combat events callback');
            pveSocket.onCombatEvents((events) => {
              // Process wave-related events for HUD
              for (const event of events) {
                const eventType = event.eventType as string;
                if ((eventType === 'wave_spawned' || eventType === 'mode_event') && event.data?.waveIndex) {
                  const waveIndex = event.data.waveIndex; // 1-based from scenario executor
                  const schedule = waveScheduleRef.current;
                  if (schedule) {
                    const waveData = schedule.waves[waveIndex - 1]; // 0-based array
                    const nextWave = schedule.waves[waveIndex]; // next wave (if any)
                    setWaveState(prev => prev ? {
                      ...prev,
                      currentWave: waveIndex,
                      enemiesRemaining: waveData?.enemyCount || 0,
                      totalEnemiesInWave: waveData?.enemyCount || 0,
                      nextWaveDelay: nextWave ? (nextWave.delay - waveData.delay) : null,
                      isSpawning: true,
                    } : null);
                    // Clear isSpawning after a short delay
                    setTimeout(() => {
                      setWaveState(prev => prev ? { ...prev, isSpawning: false } : null);
                    }, 2000);
                  }
                }
                if (event.eventType === 'unit_died' && event.data?.ownerId === 'player2') {
                  setWaveState(prev => prev ? {
                    ...prev,
                    enemiesRemaining: Math.max(0, prev.enemiesRemaining - 1),
                  } : null);
                }
              }
              // Forward to original callback
              callback(events);
            });
          },
          // PVE-FIX-007: Method to clear state callbacks on cleanup (prevents stale callbacks)
          clearStateCallbacks: () => {
            console.log('[DeploymentDemo] PvE adapter: clearing', stateCallbacks.length, 'state callbacks');
            stateCallbacks.length = 0;
          }
        };

        // Store the adapter for use by combat visualization
        pveSocketAdapterRef.current = pveAdapter;

        // Register completion handler
        pveSocket.onCompleted((result) => {
          console.log('[DeploymentDemo] PvE combat completed:', result);

          // Use server-provided stars if available, otherwise calculate client-side
          const stars = result.stars !== undefined
            ? result.stars
            : (result.outcome === 'victory'
              ? (result.stats.ticksElapsed < 3000 ? 3 : result.stats.ticksElapsed < 6000 ? 2 : 1)
              : 0);

          // Stop combat visualization
          combatActiveRef.current = false;
          setCombatActive(false);
          setShowVSScreen(false);
          setCombatInitPhase('idle');
          setWaveState(null);
          waveScheduleRef.current = null;

          // Convert PvE result to challenge result format
          setChallengeResult({
            victory: result.outcome === 'victory',
            stars,
            turnsUsed: Math.floor(result.stats.ticksElapsed / 60), // Convert ticks to turns
            unitsLost: result.stats.creatureLosses || 0,
            encounterName: `Day ${challengeDay} Challenge`,
            damageDealt: result.stats.damageDealt,
            damageTaken: result.stats.damageTaken,
            enemiesKilled: result.stats.enemiesKilled,
            rewards: result.rewards
          });

          // Clean up socket and adapter
          pveSocketAdapterRef.current = null;
          pveSocket.disconnect();
        });

        pveSocket.onError((error) => {
          console.error('[DeploymentDemo] PvE socket error:', error);
          combatActiveRef.current = false;
          setCombatActive(false);
          setShowVSScreen(false);
          setCombatInitPhase('failed');
          pveSocketAdapterRef.current = null;
          markUnready(currentPlayerId);
        });

        // Step 5: Enable combat visualization
        // Set ref FIRST for synchronous access, then state for re-render
        combatActiveRef.current = true;
        setCombatActive(true);
        deploymentRendererRef.current?.clearDeploymentZones();
        console.log('[DeploymentDemo] Combat active set to true for PvE visualization');

        // Step 6: NOW signal backend - all listeners are registered, adapter is ready
        // deployAndReady() emits both pve:deploy and pve:ready
        console.log('[DeploymentDemo] Signaling ready to backend (all listeners registered)');
        const socketPlacements = playerPlacements.map(p => ({
          creatureId: p.creatureId,
          position: { q: p.position.q, r: p.position.r }
        }));
        pveSocket.deployAndReady(socketPlacements);

        setVsLoadingProgress(100);
        setVsLoadingComplete(true);
      }
    } catch (error) {
      console.error('[DeploymentDemo] Failed to start challenge battle:', error);
      setShowVSScreen(false);
      markUnready(currentPlayerId);
    }
  }, [challengeWeekId, challengeDay, challengeAttemptId, currentPlayerId, currentPlayerState.placements, getIdToken, markReady, markUnready]);

  // =====================================================================
  // ADVENTURE MODE: handleAdventureStart
  // =====================================================================

  const handleAdventureStart = useCallback(async () => {
    if (!adventureId || !adventureEncounterId) {
      console.error('[DeploymentDemo] Missing adventure parameters');
      return;
    }

    console.log('[DeploymentDemo] Starting adventure battle...');
    markReady(currentPlayerId);

    // Prepare placements data
    const playerPlacements = currentPlayerState.placements.map(p => ({
      creatureId: p.creature.id,
      position: p.hex,
      creature: p.creature
    }));

    try {
      // Show VS screen while setting up
      setShowVSScreen(true);
      setVsLoadingProgress(25);

      // Step 1: Send each placement to backend via adventure socket
      for (const placement of playerPlacements) {
        adventureSocket.deployPlace(
          adventureEncounterId,
          placement.creatureId,
          placement.position
        );
      }
      console.log('[DeploymentDemo] Sent', playerPlacements.length, 'adventure placements');

      setVsLoadingProgress(50);

      // Step 2: Clear any stale listeners and set up state buffering
      adventureSocket.clearCombatListeners();
      console.log('[DeploymentDemo] Cleared stale adventure combat listeners');

      const stateBuffer: any[] = [];
      const stateCallbacks: Array<(state: any) => void> = [];

      // Step 3: Register state listener immediately to capture tick 0
      adventureSocket.onCombatStateUpdate((data) => {
        const state = data.state;

        if (state.tick === 0) {
          console.log('[DeploymentDemo] Adventure: received tick 0 with', state.units?.length || 0, 'units');
        }

        if (stateCallbacks.length > 0) {
          for (const callback of stateCallbacks) {
            callback(state);
          }
        } else {
          console.log('[DeploymentDemo] Adventure adapter: buffering state tick', state.tick);
          stateBuffer.push(state);
        }
      });

      // Step 4: Listen for deploy-combat-starting to get enemy data
      // CRITICAL: Enable combat visualization INSIDE this handler so enemy sprite data
      // is available in pveEnemyPlacementsRef when the combatActive useEffect fires
      adventureSocket.onDeployCombatStarting((data) => {
        console.log('[DeploymentDemo] Adventure combat starting:', data.matchId);
        setVsLoadingProgress(75);

        // Build enemy placements for VS screen
        if (data.enemyCreatures && Array.isArray(data.enemyCreatures)) {
          const enemyPlacements = (data.enemyCreatures as any[]).map((enemy: any) => ({
            creature: {
              id: enemy.creatureId || enemy.instanceId,
              name: enemy.name,
              sprite: enemy.sprite || '',
              playerId: 'player2' as const,
              battlefieldDirectionalViews: enemy.battlefieldDirectionalViews,
              size: enemy.size || 1,
            },
            hex: enemy.position,
          }));

          pveEnemyPlacementsRef.current = enemyPlacements;
          setOpponentPlacements(enemyPlacements);
          console.log('[DeploymentDemo] Adventure: set opponent placements:', enemyPlacements.length);
        }

        // Enable combat visualization now that enemy data is available
        combatActiveRef.current = true;
        setCombatActive(true);
        deploymentRendererRef.current?.clearDeploymentZones();
        console.log('[DeploymentDemo] Adventure combat active set to true (after enemy data received)');

        setVsLoadingProgress(100);
        setVsLoadingComplete(true);
      });

      // Step 5: Create adventure socket adapter (same shape as PvE adapter)
      const adventureAdapter = {
        onCombatState: (callback: (state: any) => void) => {
          console.log('[DeploymentDemo] Adventure adapter: registering state callback, buffered:', stateBuffer.length);
          stateCallbacks.push(callback);

          if (stateBuffer.length > 0) {
            console.log('[DeploymentDemo] Adventure adapter: replaying', stateBuffer.length, 'buffered states');
            for (const state of stateBuffer) {
              callback(state);
            }
            if (stateCallbacks.length === 1) {
              stateBuffer.length = 0;
            }
          }
        },
        onCombatCompleted: (callback: (result: any) => void) => {
          console.log('[DeploymentDemo] Adventure adapter: registering completion callback');
          adventureSocket.onBattleResult((result) => {
            console.log('[DeploymentDemo] Adventure adapter: received battle result');
            const combatResult = {
              winnerId: result.victory ? currentPlayerId : 'player2',
              reason: result.victory ? 'all_enemies_defeated' : 'all_allies_defeated',
              stats: { stars: result.stars },
              matchId: adventureMatchId || matchId,
            };
            callback(combatResult);
          });
        },
        onError: (callback: (error: Error) => void) => {
          console.log('[DeploymentDemo] Adventure adapter: registering error callback');
          adventureSocket.onError((error) => {
            console.error('[DeploymentDemo] Adventure adapter: received error');
            callback(new Error(error.message));
          });
        },
        signalReady: () => {
          console.log('[DeploymentDemo] Adventure adapter: signalReady (no-op)');
        },
        onEffectFrames: (_callback: (data: any) => void) => {
          console.log('[DeploymentDemo] Adventure adapter: onEffectFrames (no-op)');
        },
        requestEffectFrames: () => {
          console.log('[DeploymentDemo] Adventure adapter: requestEffectFrames (no-op)');
        },
        onCombatEvents: (callback: (events: any[]) => void) => {
          console.log('[DeploymentDemo] Adventure adapter: registering combat events callback');
          adventureSocket.onCombatEvents((data) => {
            callback(data.events as any[]);
          });
        },
        clearStateCallbacks: () => {
          console.log('[DeploymentDemo] Adventure adapter: clearing', stateCallbacks.length, 'state callbacks');
          stateCallbacks.length = 0;
        },
      };

      adventureSocketAdapterRef.current = adventureAdapter;

      // Step 6: Register completion handler for cleanup
      adventureSocket.onBattleResult((result) => {
        console.log('[DeploymentDemo] Adventure battle completed:', result);

        combatActiveRef.current = false;
        setCombatActive(false);
        setShowVSScreen(false);
        setCombatInitPhase('idle');

        setChallengeResult({
          victory: result.victory,
          stars: result.stars,
          turnsUsed: 0,
          unitsLost: 0,
          encounterName: adventureEncounter?.name || 'Adventure Encounter',
        });

        adventureSocketAdapterRef.current = null;
        adventureSocket.clearCombatListeners();
      });

      // Step 7: Signal ready to backend with placements included
      // This avoids the race condition where deploy-place async handlers haven't
      // finished writing to the placements Map before deploy-ready reads from it.
      // combatActive will be set inside the onDeployCombatStarting handler (step 4)
      // to ensure enemy sprite data is available before combat init runs
      const readyPlacements = playerPlacements.map(p => ({
        creatureId: p.creatureId,
        position: p.position,
      }));
      adventureSocket.deployReady(adventureEncounterId, readyPlacements);
      console.log('[DeploymentDemo] Adventure: signaled deploy-ready with', readyPlacements.length, 'placements');
    } catch (error) {
      console.error('[DeploymentDemo] Failed to start adventure battle:', error);
      setShowVSScreen(false);
      markUnready(currentPlayerId);
    }
  }, [adventureId, adventureEncounterId, adventureMatchId, matchId, currentPlayerId, currentPlayerState.placements, markReady, markUnready, adventureEncounter]);

  const handleReady = () => {
    if (isChallenge) {
      // Challenge mode: Start PvE battle directly
      handleChallengeStart();
    } else if (isAdventure) {
      // Adventure mode: Start adventure battle
      handleAdventureStart();
    } else {
      // PvP mode: Signal ready to opponent
      markReady(currentPlayerId);
      emitReady();
    }
  };

  const handleUnready = () => {
    markUnready(currentPlayerId);
    if (!isChallenge && !isAdventure) {
      emitUnready();
    }
  };

  const placedCreatureIds = currentPlayerState.placements.map(p => p.creature.id);
  const isLocked = deploymentStatus?.isLocked || false;
  const countdownSeconds = deploymentStatus?.countdownSeconds || null;

  // Show loading state if creatures are being loaded from library or challenge data loading
  if (loadingCreatures || challengeLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--gradient-bg-dark)',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          color: 'var(--color-text-on-dark)'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px',
            color: (isChallenge || isAdventure) ? '#4ecdc4' : 'var(--color-accent-primary)'
          }}>
            {isChallenge ? 'Loading Challenge...' : isAdventure ? 'Loading Encounter...' : 'Loading creatures...'}
          </div>
          <p style={{ color: 'var(--color-text-on-dark-muted)' }}>
            {isChallenge
              ? `Preparing Day ${challengeDay} encounter`
              : isAdventure
                ? `Preparing adventure encounter`
                : hasCreatures
                  ? `Fetching ${creatureIds.length} creature${creatureIds.length !== 1 ? 's' : ''} from library`
                  : 'Fetching all your creatures from library'}
          </p>
        </div>
      </div>
    );
  }

  // Show error state if failed to load creatures
  if (loadError) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--gradient-bg-dark)',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          color: 'var(--color-text-on-dark)',
          maxWidth: '600px'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px',
            color: 'var(--color-danger)'
          }}>
            Error Loading Creatures
          </div>
          <p style={{ color: 'var(--color-text-on-dark-muted)', marginBottom: '20px' }}>
            {loadError}
          </p>
          <button
            onClick={() => window.location.href = '/deployment'}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              borderRadius: 'var(--radius-lg)',
              border: 'none',
              background: 'var(--gradient-primary)',
              color: 'var(--color-bg-primary)',
              cursor: 'pointer',
              fontWeight: '600',
              boxShadow: 'var(--shadow-md), var(--glow-amber)'
            }}
          >
            Use Default Creatures
          </button>
        </div>
      </div>
    );
  }

  // Remove all padding on mobile during combat for full-screen battlefield
  const pagePadding = viewport.isMobile && combatActive ? '0' : '40px 20px';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--gradient-bg-dark)',
      padding: pagePadding
    }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
        {/* Header - hidden on mobile during combat to maximize battlefield space */}
        {!(viewport.isMobile && combatActive) && (
          <header style={{
            textAlign: 'center',
            marginBottom: '16px',
            color: 'var(--color-text-on-dark)'
          }}>
            <h1 style={{
              fontSize: '28px',
              margin: '0 0 8px 0',
              color: isChallenge ? '#4ecdc4' : 'var(--color-accent-primary)',
              textShadow: isChallenge ? '0 0 10px rgba(78, 205, 196, 0.5)' : 'var(--glow-amber)'
            }}>
              {isChallenge
                ? `Challenge Day ${challengeDay}`
                : 'Deploy Your Forces'}
            </h1>
            <p style={{
              fontSize: '14px',
              color: 'var(--color-text-on-dark-muted)',
              margin: '0'
            }}>
              {isChallenge ? (
                <>
                  {challengeEncounter?.name || 'Loading encounter...'} - Place your creatures and press Ready to battle!
                </>
              ) : (
                <>
                  Drag creatures onto the grid. Playing as <strong style={{ color: currentPlayerId === 'player1' ? '#3498db' : '#e74c3c' }}>
                    {currentPlayerId === 'player1' ? 'Player 1 (Blue)' : 'Player 2 (Red)'}
                  </strong>
                </>
              )}
            </p>
          </header>
        )}

        {/* Challenge Enemy Preview - shows enemies for PvE mode */}
        {isChallenge && challengeEncounter && !(viewport.isMobile && combatActive) && (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto 20px',
            padding: '16px',
            background: 'rgba(231, 76, 60, 0.1)',
            border: '1px solid rgba(231, 76, 60, 0.3)',
            borderRadius: '8px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#e74c3c', fontSize: '16px' }}>
              Enemy Forces
            </h3>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {(challengeEncounter.enemies || []).map((enemy: any, idx: number) => (
                <div key={idx} style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ color: '#fff' }}>{enemy.name}</span>
                  <span style={{
                    color: enemy.difficulty === 'boss' ? '#e74c3c'
                      : enemy.difficulty === 'elite' ? '#f39c12'
                      : '#888',
                    fontSize: '12px',
                    textTransform: 'uppercase'
                  }}>
                    {enemy.difficulty}
                  </span>
                  <span style={{ color: '#4ecdc4' }}>x{enemy.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Player Display - hidden on mobile during combat, hidden in challenge mode */}
        {!isChallenge && !(viewport.isMobile && combatActive) && (
          <PlayerDisplay
            matchId={matchId}
            currentPlayerId={currentPlayerId}
            player1Ready={deploymentStatus?.player1.isReady || false}
            player2Ready={deploymentStatus?.player2.isReady || false}
            player1Connected={isConnected && currentPlayerId === 'player1'}
            player2Connected={opponentConnected || (isConnected && currentPlayerId === 'player2')}
            player1DisplayName={player1DisplayName}
            player2DisplayName={player2DisplayName}
          />
        )}

        {/* Match Sharing + Connection Status - hidden on mobile during combat, hidden in challenge mode */}
        {!isChallenge && !(viewport.isMobile && combatActive) && (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: 1, minWidth: '400px' }}>
              <MatchSharing
                matchId={matchId}
                currentPlayerId={currentPlayerId}
              />
            </div>
            <ConnectionStatus
              isConnected={isConnected}
              opponentConnected={opponentConnected}
              opponentPlayerId={opponentPlayerId}
              error={socketError}
            />
          </div>
        )}

        {/* Main Layout: Grid with Bottom Sheet (HIDE when combat visualization is ready) */}
        {!combatVisualizationManager && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            maxWidth: '1200px',
            margin: '0 auto',
            paddingBottom: '140px' // Space for bottom sheet peek mode
          }}>
            {/* Deployment Grid */}
            <div style={{ position: 'relative' }}>
              <RendererErrorBoundary label="Deployment Grid">
              <DeploymentGridWithDragDrop
                width={1200}
                height={700}
                hexSize={38}
                showCoordinates={false}
                onHexClick={handleHexClick}
                onHexHover={handleHexHover}
                placements={currentPlayerState.placements}
                opponentPlacements={opponentPlacements}
                draggingCreature={dragState.creature}
                dragTargetHex={dragState.targetHex}
                isDragValid={dragState.isValid}
                onDrop={handleDrop}
                currentPlayerId={currentPlayerId}
                onCreatureClick={handleCreatureClick}
                onCreatureRightClick={handleCreatureRightClick}
                // Mobile tap-and-hold removal
                onRemovalHoldDetected={handleRemovalHoldDetected}
                onRemovalProgress={handleRemovalProgress}
                onRemovalComplete={handleRemovalComplete}
                onRemovalCancelled={handleRemovalCancelled}
                // Creature selected for repositioning (renders semi-transparent)
                repositionCreatureId={repositionCreatureId}
                // Dev tool: expose renderer for background position editor
                onRendererReady={(r) => {
                  deploymentRendererRef.current = r;
                  // Reset tile-loaded guard when renderer is recreated
                  deploymentTilesLoadedRef.current = false;
                  // Load PvP map tiles onto the deployment grid (r is null during cleanup/unmount)
                  if (r && pvpMapConfig?.tiles && Object.keys(pvpMapConfig.tiles).length > 0) {
                    r.loadTiles(pvpMapConfig.tiles);
                  }
                  // Load challenge map tiles onto the deployment grid
                  if (r && !pvpMapConfig?.tiles && challengeEncounter?.mapConfig?.tiles) {
                    const ct = challengeEncounter.mapConfig.tiles;
                    if (Object.keys(ct).length > 0) {
                      deploymentTilesLoadedRef.current = true;
                      r.tileBackgroundUrl = '/assets/challenges/volcanic/battlefield-bg.jpeg';
                      r.loadTiles(ct);
                      console.log('[DeploymentDemo] ✓ Challenge tiles loaded onto deployment grid');
                    }
                  }
                }}
                // Map visual settings (background image + color adjustments) — challenge or PvP
                backgroundImageOverride={challengeEncounter?.mapConfig?.visual?.backgroundImage ?? pvpMapConfig?.visual?.backgroundImage}
                bgOffsetX={challengeEncounter?.mapConfig?.visual?.bgOffsetX ?? pvpMapConfig?.visual?.bgOffsetX}
                bgOffsetY={challengeEncounter?.mapConfig?.visual?.bgOffsetY ?? pvpMapConfig?.visual?.bgOffsetY}
                bgScale={challengeEncounter?.mapConfig?.visual?.bgScale ?? pvpMapConfig?.visual?.bgScale}
                bgBrightness={challengeEncounter?.mapConfig?.visual?.bgBrightness ?? pvpMapConfig?.visual?.bgBrightness}
                bgContrast={challengeEncounter?.mapConfig?.visual?.bgContrast ?? pvpMapConfig?.visual?.bgContrast}
                bgSaturation={challengeEncounter?.mapConfig?.visual?.bgSaturation ?? pvpMapConfig?.visual?.bgSaturation}
                bgHue={challengeEncounter?.mapConfig?.visual?.bgHue ?? pvpMapConfig?.visual?.bgHue}
                gridColumns={adventureEncounter?.battlefieldConfig?.width ?? challengeEncounter?.mapConfig?.columns ?? pvpMapConfig?.columns}
                gridRows={adventureEncounter?.battlefieldConfig?.height ?? challengeEncounter?.mapConfig?.rows ?? pvpMapConfig?.rows}
                deploymentZoneConfig={adventureEncounter?.battlefieldConfig ? {
                  orientation: (adventureEncounter.battlefieldConfig.orientation || 'left_vs_right') as 'left_vs_right' | 'top_vs_bottom',
                  playerDeployCols: adventureEncounter.battlefieldConfig.playerDeployCols,
                  playerDeployRows: adventureEncounter.battlefieldConfig.playerDeployRows,
                  enemyDeployRows: adventureEncounter.battlefieldConfig.enemyDeployRows,
                  gridHeight: adventureEncounter.battlefieldConfig.height,
                  gridWidth: adventureEncounter.battlefieldConfig.width,
                } : challengeEncounter?.mapConfig?.playerSpawn ? (() => {
                  const p1Cols = challengeEncounter.mapConfig.playerSpawn.map((h: any) => h.q);
                  const p2Hexes = Array.isArray(challengeEncounter.mapConfig.enemySpawn)
                    ? challengeEncounter.mapConfig.enemySpawn.map((h: any) => h.position?.q ?? h.q)
                    : [];
                  return {
                    orientation: 'left_vs_right' as const,
                    player1ColRange: { min: Math.min(...p1Cols), max: Math.max(...p1Cols) },
                    player2ColRange: p2Hexes.length > 0
                      ? { min: Math.min(...p2Hexes), max: Math.max(...p2Hexes) }
                      : undefined,
                    gridWidth: challengeEncounter.mapConfig.columns,
                    gridHeight: challengeEncounter.mapConfig.rows,
                  };
                })() : pvpMapConfig?.playerSpawn ? (() => {
                  const p1Cols = pvpMapConfig.playerSpawn.map((h: any) => h.q);
                  const p2Hexes = Array.isArray(pvpMapConfig.enemySpawn)
                    ? pvpMapConfig.enemySpawn.map((h: any) => h.position?.q ?? h.q)
                    : [];
                  return {
                    orientation: 'left_vs_right' as const,
                    player1ColRange: { min: Math.min(...p1Cols), max: Math.max(...p1Cols) },
                    player2ColRange: { min: Math.min(...p2Hexes), max: Math.max(...p2Hexes) },
                    gridWidth: pvpMapConfig.columns,
                    gridHeight: pvpMapConfig.rows,
                  };
                })() : { orientation: 'left_vs_right' as const }}
              />
              </RendererErrorBoundary>

              {/* Series Record Display - Shows after first battle */}
              {seriesRecord && (
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(30, 42, 58, 0.95)',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border-light)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  zIndex: 100,
                  color: 'var(--color-text-on-dark)'
                }}>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-on-dark-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    Round {roundNumber}
                  </span>
                  <span style={{ color: 'var(--color-border-light)' }}>|</span>
                  <span style={{ color: 'var(--color-accent-primary)', fontWeight: 'bold' }}>
                    You: {currentPlayerId === 'player1' ? seriesRecord.player1Wins : seriesRecord.player2Wins}
                  </span>
                  <span style={{ color: 'var(--color-text-on-dark-muted)' }}>-</span>
                  <span style={{ color: 'var(--color-danger)', fontWeight: 'bold' }}>
                    Opponent: {currentPlayerId === 'player1' ? seriesRecord.player2Wins : seriesRecord.player1Wins}
                  </span>
                  {seriesRecord.draws > 0 && (
                    <span style={{ fontSize: '12px', color: 'var(--color-text-on-dark-muted)' }}>
                      ({seriesRecord.draws} draws)
                    </span>
                  )}
                </div>
              )}

              {/* Ready Panel - Overlaid on grid */}
              <ReadyPanel
                currentPlayerId={currentPlayerId}
                player1Ready={isChallenge ? currentPlayerState.isReady : (deploymentStatus?.player1.isReady || false)}
                player2Ready={isChallenge ? true : (deploymentStatus?.player2.isReady || false)} // In challenge mode, "opponent" is always ready
                isLocked={isLocked}
                countdownSeconds={countdownSeconds}
                canMarkReady={isChallenge ? (getPlacementCount(currentPlayerId) >= 1) : canMarkReady(currentPlayerId)}
                onReady={handleReady}
                onUnready={handleUnready}
              />

              {/* Battlefield Navigation Help - L4-HELP */}
              <BattlefieldHelp />
            </div>
          </div>
        )}

        {/* Bottom Sheet Creature Roster (fixed positioned, shows during deployment) */}
        {!combatVisualizationManager && (
          <BottomSheetRoster
            deployedCount={getPlacementCount(currentPlayerId)}
            maxDeployment={creatureLimit}
            deployedCreatureIds={deployedCreatureIds}
            onDragStart={handleGalleryDragStart}
            onDragEnd={handleGalleryDragEnd}
            onSelect={handleCreatureSelect}
            selectedCreatureId={selectedCreatureId}
            onSelectionClear={clearSelection}
          />
        )}

        {/* Combat Viewer (REPLACES deployment grid when visualization is ready) */}
        {combatVisualizationManager && (
          <RendererErrorBoundary label="Combat Renderer">
          <div style={{
            marginTop: viewport.isMobile ? '0' : '30px',
            padding: viewport.isMobile ? '0' : '20px',
            background: viewport.isMobile ? 'transparent' : 'rgba(139, 0, 0, 0.2)',
            borderRadius: viewport.isMobile ? '0' : '8px',
            border: viewport.isMobile ? 'none' : '2px solid #e74c3c',
            boxShadow: viewport.isMobile ? 'none' : '0 0 20px rgba(231, 76, 60, 0.3)',
            overflow: 'hidden'
          }}>
            {/* Mobile: Fixed HUD bar at top */}
            {viewport.isMobile && combatActive && (
              <MobileCombatHUD
                roundNumber={roundNumber}
                seriesRecord={seriesRecord}
                currentPlayerId={currentPlayerId}
                onMenuOpen={() => setIsCombatMenuOpen(true)}
              />
            )}

            {/* Desktop: Combat Header with Series Record */}
            {!viewport.isMobile && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '20px',
                margin: '0 0 15px 0',
                flexWrap: 'wrap'
              }}>
                <h3 style={{
                  margin: 0,
                  color: '#e74c3c',
                  fontSize: '24px',
                  textAlign: 'center',
                  textShadow: '0 0 10px rgba(231, 76, 60, 0.5)'
                }}>
                  Combat in Progress
                </h3>

                {/* Series Record Display during combat */}
                {seriesRecord && (
                  <div style={{
                    background: 'rgba(30, 42, 58, 0.95)',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '14px'
                  }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                      R{roundNumber}
                    </span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>|</span>
                    <span style={{ color: '#f5a623', fontWeight: 'bold' }}>
                      {currentPlayerId === 'player1' ? seriesRecord.player1Wins : seriesRecord.player2Wins}
                    </span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>-</span>
                    <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                      {currentPlayerId === 'player1' ? seriesRecord.player2Wins : seriesRecord.player1Wins}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Combat Visualization Canvas and Log */}
            <div style={{
              display: 'flex',
              gap: viewport.isMobile ? '0' : '8px',
              marginBottom: viewport.isMobile ? '0' : '20px',
              overflow: 'hidden'
            }}>
              {/* Team Ability Panel — Desktop: left sidebar */}
              {FRONTEND_FEATURE_FLAGS.USE_TEAM_ABILITY_PANEL && combatActive && !combatResult && !viewport.isMobile && (
                <TeamAbilityPanel
                  combatState={combatState}
                  currentPlayerId={currentPlayerId}
                  isMobile={false}
                  isPortrait={false}
                  onCreatureClick={handleCreaturePanTo}
                />
              )}
              {/* Combat Grid Canvas with Zoom Controls */}
              <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                <div
                  ref={combatCanvasRef}
                  style={{
                    background: '#1a1a1a',
                    borderRadius: '4px',
                    border: '1px solid #444',
                    minHeight: `${combatCanvasSize.height}px`,
                    maxWidth: `${combatCanvasSize.width}px`,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    touchAction: 'none'  // Prevent browser pinch-zoom, let our handler control it
                  }}
                />
                {/* Zoom controls overlay - L4-ZOOM-005 */}
                {combatGridRenderer && (
                  <ZoomControls
                    onZoomIn={handleCombatZoomIn}
                    onZoomOut={handleCombatZoomOut}
                  />
                )}
                {/* Battlefield Navigation Help - L4-HELP */}
                <BattlefieldHelp />
                {/* Wave Indicator overlay for challenge battles (top-right corner) */}
                {isChallenge && combatActive && waveState && (
                  <div style={{
                    position: 'absolute',
                    top: viewport.isMobile ? '4px' : '8px',
                    right: viewport.isMobile ? '4px' : '8px',
                    zIndex: 20,
                    pointerEvents: 'auto',
                  }}>
                    <WaveIndicator
                      currentWave={waveState.currentWave}
                      totalWaves={waveState.totalWaves}
                      enemiesRemaining={waveState.enemiesRemaining}
                      totalEnemies={waveState.totalEnemiesInWave}
                      timeUntilNextWave={waveState.nextWaveDelay !== null
                        ? Math.ceil(waveState.nextWaveDelay / 60)
                        : undefined}
                      isSpawning={waveState.isSpawning}
                    />
                  </div>
                )}
                {/* Combat Log UI - TASK-VIS-004 (hidden on mobile - post-battle log is sufficient) */}
                {combatActive && !combatResult && !viewport.isMobile && <CombatLog maxHeight={180} width={280} />}
                {/* Kill Notification Stack - L4-KILL-005 */}
                {combatActive && (
                  <KillNotificationStack manager={killNotificationManagerRef.current} />
                )}
                {/* Mobile Combat Menu - slide-up bottom sheet */}
                {viewport.isMobile && (
                  <MobileCombatMenu
                    isOpen={isCombatMenuOpen}
                    onClose={() => setIsCombatMenuOpen(false)}
                    onLeaveBattle={handleReturnToLobby}
                  />
                )}
              </div>
            </div>

            {/* Team Ability Panel — Mobile portrait: fixed bottom bar */}
            {FRONTEND_FEATURE_FLAGS.USE_TEAM_ABILITY_PANEL && combatActive && !combatResult && viewport.isMobile && (
              <TeamAbilityPanel
                combatState={combatState}
                currentPlayerId={currentPlayerId}
                isMobile={true}
                isPortrait={viewport.isPortrait}
                onCreatureClick={handleCreaturePanTo}
              />
            )}

            {/* Combat Stats Summary - hidden on mobile to maximize battlefield space */}
            {combatState && !viewport.isMobile && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                color: '#ecf0f1'
              }}>
                {/* Player 1 Units */}
                <div style={{
                  padding: '15px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '4px',
                  border: '1px solid #3498db'
                }}>
                  <strong style={{ color: '#3498db' }}>Player 1 Units:</strong>{' '}
                  <span style={{ fontSize: '20px', color: '#fff' }}>
                    {combatState.units?.filter((u: any) => u.ownerId === 'player1' && u.status === 'alive').length || 0}
                  </span>
                </div>

                {/* Player 2 Units */}
                <div style={{
                  padding: '15px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '4px',
                  border: '1px solid #e74c3c'
                }}>
                  <strong style={{ color: '#e74c3c' }}>Player 2 Units:</strong>{' '}
                  <span style={{ fontSize: '20px', color: '#fff' }}>
                    {combatState.units?.filter((u: any) => u.ownerId === 'player2' && u.status === 'alive').length || 0}
                  </span>
                </div>

                {/* Combat Winner */}
                {combatState.winner && (
                  <div style={{
                    padding: '15px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '4px',
                    border: '2px solid #2ecc71',
                    gridColumn: '1 / -1',
                    textAlign: 'center'
                  }}>
                    <strong style={{ color: '#2ecc71', fontSize: '24px' }}>
                      Winner: {combatState.winner === 'player1' ? 'Player 1' : combatState.winner === 'player2' ? 'Player 2' : 'Draw'}
                    </strong>
                  </div>
                )}
              </div>
            )}

            {/* Raw Combat State (for debugging) - hidden on mobile */}
            {combatState && !viewport.isMobile && (
              <details style={{ marginTop: '20px' }}>
                <summary style={{
                  cursor: 'pointer',
                  color: '#95a5a6',
                  fontSize: '14px',
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  userSelect: 'none'
                }}>
                  Show Full Combat State (Debug)
                </summary>
                <pre style={{
                  marginTop: '10px',
                  padding: '15px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  borderRadius: '4px',
                  color: '#2ecc71',
                  fontSize: '12px',
                  overflow: 'auto',
                  maxHeight: '400px',
                  border: '1px solid #34495e'
                }}>
                  {JSON.stringify(combatState, null, 2)}
                </pre>
              </details>
            )}
          </div>
          </RendererErrorBoundary>
        )}

      </div>

      {/* VS Screen Overlay - displays during loading transition */}
      {showVSScreen && (
        <VSScreenOverlay
          playerCreatures={currentPlayerState.placements}
          opponentCreatures={opponentPlacements}
          loadingProgress={vsLoadingProgress}
          isLoadingComplete={vsLoadingComplete}
          combatInitPhase={combatInitPhase}
          onComplete={handleVSScreenComplete}
          playerName={user?.displayName || 'You'}
          opponentName="Opponent"
        />
      )}

      {/* Victory Overlay - displays when combat completes (PvP) */}
      {combatResult && (
        <VictoryOverlay
          result={combatResult}
          currentPlayerId={currentPlayerId}
          onReturnToLobby={handleReturnToLobby}
          onFightAgain={handleFightAgain}
          winnerDisplayName={
            combatResult.winner === 'draw'
              ? undefined
              : combatResult.winner === currentPlayerId
                ? (user?.displayName || 'You')
                : 'Opponent'
          }
          seriesRecord={seriesRecord ?? undefined}
        />
      )}

      {/* Challenge Result Overlay - displays when PvE challenge completes */}
      {challengeResult && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
            maxWidth: '500px',
            border: challengeResult.victory
              ? '2px solid #4ecdc4'
              : '2px solid #e74c3c',
            boxShadow: challengeResult.victory
              ? '0 0 40px rgba(78, 205, 196, 0.3)'
              : '0 0 40px rgba(231, 76, 60, 0.3)'
          }}>
            {/* Result Header */}
            <div style={{
              fontSize: '48px',
              marginBottom: '8px'
            }}>
              {challengeResult.victory ? '🏆' : '💀'}
            </div>
            <h2 style={{
              fontSize: '32px',
              margin: '0 0 8px 0',
              color: challengeResult.victory ? '#4ecdc4' : '#e74c3c',
              textShadow: challengeResult.victory
                ? '0 0 20px rgba(78, 205, 196, 0.5)'
                : '0 0 20px rgba(231, 76, 60, 0.5)'
            }}>
              {challengeResult.victory ? 'VICTORY!' : 'DEFEAT'}
            </h2>
            <p style={{
              color: '#888',
              margin: '0 0 24px 0',
              fontSize: '16px'
            }}>
              {challengeResult.encounterName || `Day ${challengeDay}`}
            </p>

            {/* Stars (if victory) */}
            {challengeResult.victory && (
              <div style={{
                fontSize: '36px',
                marginBottom: '24px',
                letterSpacing: '8px'
              }}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <span key={i} style={{
                    opacity: i < challengeResult.stars ? 1 : 0.3
                  }}>
                    ⭐
                  </span>
                ))}
              </div>
            )}

            {/* Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '12px',
                borderRadius: '8px'
              }}>
                <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>
                  Turns
                </div>
                <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>
                  {challengeResult.turnsUsed}
                </div>
              </div>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '12px',
                borderRadius: '8px'
              }}>
                <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>
                  Losses
                </div>
                <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>
                  {challengeResult.unitsLost}
                </div>
              </div>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '12px',
                borderRadius: '8px'
              }}>
                <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>
                  Damage Dealt
                </div>
                <div style={{ color: '#4ecdc4', fontSize: '20px', fontWeight: 'bold' }}>
                  {challengeResult.damageDealt?.toLocaleString() || '0'}
                </div>
              </div>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '12px',
                borderRadius: '8px'
              }}>
                <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>
                  Enemies Killed
                </div>
                <div style={{ color: '#44bd32', fontSize: '20px', fontWeight: 'bold' }}>
                  {challengeResult.enemiesKilled || 0}
                </div>
              </div>
            </div>

            {/* Rewards (if any) */}
            {challengeResult.rewards && challengeResult.rewards.length > 0 && (
              <div style={{
                background: 'rgba(255, 215, 0, 0.1)',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  color: '#ffd700',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Rewards Earned
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {challengeResult.rewards.map((reward, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#fff'
                    }}>
                      <span style={{ fontSize: '18px' }}>
                        {reward.type === 'currency' ? '💰' :
                         reward.type === 'creature_unlock' ? '🎁' :
                         reward.type === 'cosmetic' ? '✨' : '🏆'}
                      </span>
                      <span>{reward.description || reward.id}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => {
                  setChallengeResult(null);
                  if (isAdventure && adventureId) {
                    navigate(`/adventure/map/${adventureId}`);
                  } else {
                    navigate('/challenges');
                  }
                }}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: '1px solid #4ecdc4',
                  background: 'transparent',
                  color: '#4ecdc4',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {isAdventure ? 'Back to Map' : 'Back to Hub'}
              </button>
              {!challengeResult.victory && (
                <button
                  onClick={() => {
                    setChallengeResult(null);
                    markUnready(currentPlayerId);
                  }}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #4ecdc4, #44bd32)',
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeploymentGridDemoPage;
